// Dashboard.js — TSI Business Dashboard — v6.5 (Session 10)
// Base: v6.1 — only TrendsCard section replaced
// Changes from v6.1:
//   1. TrendsCard rewritten — 4 scales, smart axis, 4 chart modes
//   2. 4 text mode buttons: Combo | All Lines | All Bars | Waterfall
//   3. Drawn axes for HC (0–350) and Ratio (-20..+20) when needed
//   4. Dollar scale symmetric, tooltip shows actual values
//   5. gaRatio/smRatio now in % (×100), S&M color #ca8a04

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PnLChatWidget from './PnLChatWidget';
import UploadPage from './UploadPage';


const API    = (process.env.REACT_APP_API_URL || '');
const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
const YEARS  = [2025, 2026];

const QUARTERS = [
  { label: "Q1 (Jan–Mar)", value: "Q1", months: ["JAN","FEB","MAR"] },
  { label: "Q2 (Apr–Jun)", value: "Q2", months: ["APR","MAY","JUN"] },
  { label: "Q3 (Jul–Sep)", value: "Q3", months: ["JUL","AUG","SEP"] },
  { label: "Q4 (Oct–Dec)", value: "Q4", months: ["OCT","NOV","DEC"] },
];

const HALF_YEARS = [
  { label: "H1 (Jan–Jun)", value: "H1", months: ["JAN","FEB","MAR","APR","MAY","JUN"] },
  { label: "H2 (Jul–Dec)", value: "H2", months: ["JUL","AUG","SEP","OCT","NOV","DEC"] },
];

// ── Period label helpers ─────────────────────────────────────────
function getQuarterLabel(month) {
  const idx = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"].indexOf(month.toUpperCase());
  if (idx <= 2)  return "Q1";
  if (idx <= 5)  return "Q2";
  if (idx <= 8)  return "Q3";
  return "Q4";
}

// Returns { periodLabel, sumLabel, avgLabel } for KPI tiles
function buildTileLabels(activeFilter, month, year, quarter, halfYear, monthsWithData) {
  const mwd = monthsWithData || 1;

  if (activeFilter === "quarter" && quarter) {
    const periodLabel = `${quarter} ${year}`;
    return {
      periodLabel,
      sumLabel:  (lbl) => `Sum of ${lbl}`,
      // avgLabel:  () => `Average (${mwd} month${mwd!==1?"s":""})`,
      avgLabel:  (lbl) => lbl ? `${lbl} Average (${mwd} month${mwd!==1?"s":""})` : `Average (${mwd} month${mwd!==1?"s":""})`
    };
  }
  if (activeFilter === "halfyear" && halfYear) {
    const periodLabel = `${halfYear} ${year}`;
    return {
      periodLabel,
      sumLabel:  (lbl) => `Sum of ${lbl}`,
      // avgLabel:  () => `Average (${mwd} month${mwd!==1?"s":""})`,

      // avgLabel:  (lbl) => lbl ? `${lbl} Average (${mwd} month${mwd!==1?"s":""})` : `Average (${mwd} month${mwd!==1?"s":""})`
      avgLabel:  (lbl) => lbl ? `Average of ${lbl} (${mwd} month${mwd!==1?"s":""})` : `Average (${mwd} month${mwd!==1?"s":""})`
    };
  }
  if (activeFilter === "last3") {
    // Build MAR→MAY 2025 style label
    const idx = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"].indexOf(month.toUpperCase());
    const months3 = [];
    for(let i=2;i>=0;i--){
      let mIdx=idx-i; let y=year;
      if(mIdx<0){mIdx+=12;y=year-1;}
      months3.push({m:["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][mIdx],y});
    }
    const startM = months3[0].m.slice(0,3);
    const endM   = months3[2].m.slice(0,3);
    const endY   = months3[2].y;
    const periodLabel = `Last 3M · ${startM}-${endM} ${endY}`;
    return {
      periodLabel,
      sumLabel:  (lbl) => `Sum of ${lbl}`,
      avgLabel:  (lbl) => lbl ? `${lbl} Average (${mwd} month${mwd!==1?"s":""})` : `Average (${mwd} month${mwd!==1?"s":""})`,
    };
  }
  // Single month
  const periodLabel = `${month.toUpperCase()} ${year}`;
  return {
    periodLabel,
    sumLabel:  (lbl) => lbl,
    avgLabel:  () => null,
  };
}

// Returns subtitle for SCard — Option C style (subtitle below title)
function getCardSubtitle(activeFilter, month, year, quarter, halfYear, type="sum") {
  const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  if (activeFilter === "month") return ""; // single month — no subtitle

  let periodStr = "";
  let typeStr   = type === "sum" ? "Sum of values" : "";

  if (activeFilter === "quarter" && quarter) {
    periodStr = `${quarter} ${year}`;
    typeStr   = type === "sum" ? "Sum of values" : "Average of values";
  } else if (activeFilter === "halfyear" && halfYear) {
    periodStr = `${halfYear} ${year}`;
    typeStr   = type === "sum" ? "Sum of values" : "Average of values";
  } else if (activeFilter === "last3") {
    const idx = MONTHS.indexOf(month.toUpperCase());
    let startIdx = idx - 2; let startYear = year;
    if(startIdx < 0){ startIdx += 12; startYear = year - 1; }
    const startM = MONTHS[startIdx].slice(0,3);
    const endM   = month.slice(0,3).toUpperCase();
    periodStr = `${startM} → ${endM} ${year}`;
    typeStr   = type === "sum" ? "Sum of values" : "Average of values";
  }

  if (!periodStr) return "";
  return `${periodStr} · ${typeStr}`;
}

// Returns period suffix for card titles
// e.g. "Q1 2025 (Sum)" or "Q1 2025 (Avg)" or "" for single month
function getCardPeriodLabel(activeFilter, month, year, quarter, halfYear, type="sum") {
  const suffix = type === "sum" ? "(Sum)" : "(Avg)";
  if (activeFilter === "quarter" && quarter) {
    return `${quarter} ${year} ${suffix}`;
  }
  if (activeFilter === "halfyear" && halfYear) {
    return `${halfYear} ${year} ${suffix}`;
  }
  if (activeFilter === "last3") {
    const idx = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"].indexOf(month.toUpperCase());
    let startIdx = idx - 2; let startYear = year;
    if(startIdx < 0){ startIdx += 12; startYear = year - 1; }
    const startM = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][startIdx].slice(0,3);
    const endM   = month.slice(0,3).toUpperCase();
    return `${startM}-${endM} ${year} ${suffix}`;
  }
  // Single month — no suffix needed
  return "";
}

function getLast3Label(month, year) {
  const idx = MONTHS.indexOf(month);
  const months = [];
  for (let i = 2; i >= 0; i--) {
    let mIdx = idx - i;
    let y = year;
    if (mIdx < 0) { mIdx += 12; y = year - 1; }
    months.push(`${MONTHS[mIdx]} ${y}`);
  }
  return months.join(" → ");
}

const REV_C  = ["#f97316","#6366f1","#10b981","#ec4899","#f59e0b","#06b6d4"];
const COGS_C = ["#ef4444","#8b5cf6","#14b8a6","#f97316","#84cc16","#fb7185"];
const OPEX_C = ["#3b82f6","#10b981","#f59e0b","#a855f7","#f43f5e","#22d3ee"];
const DRILL_C= ["#f97316","#6366f1","#10b981","#ec4899","#f59e0b","#06b6d4","#8b5cf6","#84cc16","#ef4444","#14b8a6","#a855f7","#fb923c"];

const TSI_ORANGE = "#f97316";
const TSI_RED    = "#ef4444";
const TSI_BLUE   = "#3b82f6";

const DARK = {
  bg:"#030712",bgGrad:"radial-gradient(ellipse at 20% 50%,#0d1b3e 0%,#030712 60%)",
  sidebar:"#050e24",sidebarBord:"rgba(255,255,255,0.06)",
  card:"rgba(10,18,40,0.95)",cardBg:"#0a1228",cardBorder:"rgba(255,255,255,0.07)",
  cardHover:"rgba(255,255,255,0.04)",
  text1:"#FFFFFF",text2:"#E2E8F0",text3:"#94A3B8",
  accent:TSI_ORANGE,accentBlue:TSI_BLUE,accentRed:TSI_RED,
  green:"#22c55e",red:"#f43f5e",
  divider:"rgba(148,163,184,0.08)",vbox:"rgba(255,255,255,0.05)",
  vboxBorder:"rgba(255,255,255,0.1)",rowHover:"rgba(249,115,22,0.05)",
  navBg:"rgba(3,7,18,0.97)",navBorder:"rgba(255,255,255,0.07)",
  glass:"rgba(255,255,255,0.03)",inputBg:"rgba(249,115,22,0.1)",
};

const LIGHT = {
  bg:"#f0f4ff",bgGrad:"radial-gradient(ellipse at 20% 20%,#dbeafe 0%,#f0f4ff 60%)",
  sidebar:"#0c1a3a",sidebarBord:"rgba(255,255,255,0.1)",
  card:"#ffffff",cardBg:"#ffffff",cardBorder:"rgba(99,102,241,0.1)",
  cardHover:"rgba(99,102,241,0.02)",
  text1:"#020617",text2:"#1E293B",text3:"#334155",
  accent:TSI_ORANGE,accentBlue:TSI_BLUE,accentRed:TSI_RED,
  green:"#16a34a",red:"#dc2626",
  divider:"rgba(15,23,42,0.07)",vbox:"rgba(249,115,22,0.05)",
  vboxBorder:"rgba(249,115,22,0.2)",rowHover:"rgba(99,102,241,0.04)",
  navBg:"#0c1a3a",navBorder:"rgba(255,255,255,0.1)",
  glass:"rgba(255,255,255,0.7)",inputBg:"rgba(249,115,22,0.1)",
};

let dark_ref = true;

const fmtM = v=>{
  if(v==null) return "—";
  if(Math.abs(v)>=1_000_000) return `$${(v/1_000_000).toFixed(2)}M`;
  if(Math.abs(v)>=1_000)     return `$${(v/1_000).toFixed(2)}K`;
  return `$${Math.round(v)}`;
};
const momPct   = (c,p)=>(!p||p===0||c==null)?null:((c-p)/Math.abs(p))*100;
const toMon    = m=>({JAN:"Jan",FEB:"Feb",MAR:"Mar",APR:"Apr",MAY:"May",JUN:"Jun",JUL:"Jul",AUG:"Aug",SEP:"Sep",OCT:"Oct",NOV:"Nov",DEC:"Dec"}[m]||m);
const fmtEbita = v=>v!=null?`${Number(v).toFixed(2)}%`:"—";
const fmtInt   = v=>v!=null?`${Math.round(v)}`:"—";
// const fmtLoc   = v=>v!=null?`$${Number(v).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`:"—";
// const fmtLoc = v=>v!=null?`$${Math.round(v).toLocaleString()}`:"—";
const fmtLoc = v=>v!=null?`$${Number(v).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`:"—";
function Counter({val,fmt=fmtM}){
  const [d,setD]=useState(val);
  const pv=useRef(val||0);
  useEffect(()=>{
    if(val==null) return;
    const from=pv.current||0,to=val,t0=performance.now();
    const tick=now=>{
      const p=Math.min((now-t0)/900,1),e=1-Math.pow(1-p,3);
      setD(from+(to-from)*e);
      if(p<1) requestAnimationFrame(tick); else pv.current=to;
    };
    requestAnimationFrame(tick);
  },[val]);
  return <span>{fmt(d)}</span>;
}

function BadgeInner({value,prev,lower=false,T}){
  const p=momPct(value,prev);
  if(p==null||Math.abs(p)<0.01) return null;
  const good = lower ? p<0 : p>0;
  return(
    <span style={{fontSize:9.5,fontWeight:700,padding:'3px 8px',borderRadius:20,whiteSpace:'nowrap',
      background:good?'rgba(34,197,94,0.14)':'rgba(244,63,94,0.14)',
      color:good?T.green:T.red,
      border:'1px solid '+(good?'rgba(34,197,94,0.3)':'rgba(244,63,94,0.3)'),
      letterSpacing:0.3}}>
      {p>0?'▲':'▼'} {Math.abs(p).toFixed(2)}%
    </span>
  );
}

// function ValBox({value,fmt=fmtM,T,width=110}){
//   return(
//     <div style={{width,padding:"5px 0",textAlign:"center",flexShrink:0,
//       background:"transparent",border:"none",
//       borderRadius:9,fontWeight:800,fontSize:13,color:T.text1,
//       fontFamily:"'JetBrains Mono',Consolas,monospace",letterSpacing:0.5}}>
//       <Counter val={value} fmt={fmt}/>
//     </div>
//   );
// }
function ValBox({value,fmt=fmtM,T,width=110,valueColor=null}){
  return(
    <div style={{width,padding:"5px 0",textAlign:"center",flexShrink:0,
      background:"transparent",border:"none",
      borderRadius:9,fontWeight:800,fontSize:13,color:valueColor||T.text1,
      fontFamily:"'JetBrains Mono',Consolas,monospace",letterSpacing:0.5}}>
      <Counter val={value} fmt={fmt}/>
    </div>
  );
}

// function ValRow({value,prev,lower=false,fmt=fmtM,T,width=110}){
//   return(
//     <div style={{display:"flex",alignItems:"center",flexShrink:0,width:189}}>
//       <ValBox value={value} fmt={fmt} T={T} width={width}/>
function ValRow({value,prev,lower=false,fmt=fmtM,T,width=110,valueColor=null}){
  return(
    <div style={{display:"flex",alignItems:"center",flexShrink:0,width:189}}>
      <ValBox value={value} fmt={fmt} T={T} width={width} valueColor={valueColor}/>
      <div style={{width:79,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"flex-start",paddingLeft:7}}>
        <BadgeInner value={value} prev={prev} lower={lower} T={T}/>
      </div>
    </div>
  );
}

function MiniDonut({data,colors,size=42}){
  const [tip,setTip]=useState(null);
  const entries=Object.entries(data||{}).filter(([,v])=>Math.abs(v)>0);
  const total=entries.reduce((s,[,v])=>s+Math.abs(v),0);
  if(!total) return null;
  const cx=size/2,cy=size/2,R=size*0.43,r=size*0.27;
  let a=-Math.PI/2;
  const slices=entries.map(([label,value],i)=>{
    const p=Math.abs(value)/total,sw=p*2*Math.PI;
    const x1=cx+R*Math.cos(a),y1=cy+R*Math.sin(a); a+=sw;
    const x2=cx+R*Math.cos(a),y2=cy+R*Math.sin(a);
    const xi1=cx+r*Math.cos(a),yi1=cy+r*Math.sin(a);
    const xi2=cx+r*Math.cos(a-sw),yi2=cy+r*Math.sin(a-sw);
    const lg=sw>Math.PI?1:0;
    return{path:`M ${x1} ${y1} A ${R} ${R} 0 ${lg} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${r} ${r} 0 ${lg} 0 ${xi2} ${yi2} Z`,color:colors[i%colors.length],label,value,p};
  });
  return(
    <div style={{position:"relative",flexShrink:0}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{cursor:"pointer"}}>
        {slices.map((s,i)=>(
          <path key={i} d={s.path} fill={s.color} stroke={dark_ref?"rgba(3,7,18,0.5)":"rgba(255,255,255,0.6)"} strokeWidth={0.8}
            style={{opacity:tip&&tip.i!==i?0.28:1,transition:"opacity 0.15s"}}
            onMouseEnter={()=>setTip({i,...s})} onMouseLeave={()=>setTip(null)}/>
        ))}
        <circle cx={cx} cy={cy} r={r-0.5} fill={dark_ref?"#0a1228":"#ffffff"}/>
      </svg>
      {tip&&(
        <div style={{position:"absolute",bottom:"calc(100% + 6px)",left:"50%",transform:"translateX(-50%)",
          zIndex:999,background:dark_ref?"#0c1832":"#fff",
          border:`1px solid ${tip.color}50`,borderRadius:9,padding:"7px 11px",whiteSpace:"nowrap",
          boxShadow:`0 8px 28px rgba(0,0,0,0.4),0 0 0 1px ${tip.color}25`,pointerEvents:"none"}}>
          <p style={{margin:0,fontSize:9.5,color:tip.color,fontWeight:700}}>{tip.label}</p>
          <p style={{margin:"2px 0 0",fontSize:10.5,color:dark_ref?"#f8fafc":"#0f172a",fontWeight:800,fontFamily:"monospace"}}>{fmtM(tip.value)} ({(tip.p*100).toFixed(1)}%)</p>
        </div>
      )}
    </div>
  );
}

function FullDonut({data,colors,T,size=155}){
  const [hov,setHov]=useState(null);
  const entries=Object.entries(data||{}).filter(([,v])=>Math.abs(v)>0);
  const total=entries.reduce((s,[,v])=>s+Math.abs(v),0);
  if(!total) return null;
  const cx=size/2,cy=size/2,R=size*0.40,r=size*0.26;
  let a=-Math.PI/2;
  const slices=entries.map(([label,value],i)=>{
    const p=Math.abs(value)/total,sw=p*2*Math.PI;
    const x1=cx+R*Math.cos(a),y1=cy+R*Math.sin(a); a+=sw;
    const x2=cx+R*Math.cos(a),y2=cy+R*Math.sin(a);
    const xi1=cx+r*Math.cos(a),yi1=cy+r*Math.sin(a);
    const xi2=cx+r*Math.cos(a-sw),yi2=cy+r*Math.sin(a-sw);
    const lg=sw>Math.PI?1:0;
    return{path:`M ${x1} ${y1} A ${R} ${R} 0 ${lg} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${r} ${r} 0 ${lg} 0 ${xi2} ${yi2} Z`,color:colors[i%colors.length],label,value,p};
  });
  return(
    <div style={{display:"flex",alignItems:"center",gap:16,padding:"14px 18px"}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{flexShrink:0}}>
        {slices.map((s,i)=>(
          <path key={i} d={s.path} fill={s.color}
            stroke={dark_ref?"rgba(3,7,18,0.5)":"rgba(255,255,255,0.8)"} strokeWidth={2}
            opacity={hov!==null&&hov!==i?0.2:1}
            style={{transition:"all 0.22s cubic-bezier(0.34,1.56,0.64,1)",cursor:"pointer",
              transform:hov===i?"scale(1.07)":"scale(1)",transformOrigin:`${cx}px ${cy}px`,
              filter:hov===i?`drop-shadow(0 0 12px ${s.color})`:"none"}}
            onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}/>
        ))}
        <circle cx={cx} cy={cy} r={r-2} fill={T.cardBg}/>
        <text x={cx} y={cy-7} textAnchor="middle" fontSize={7.5} fill={T.text3} fontWeight={700} letterSpacing={1}>TOTAL</text>
        {hov!==null?(
          <>
            <text x={cx} y={cy+7} textAnchor="middle" fontSize={7} fill={slices[hov].color} fontWeight={700}>{slices[hov].label.slice(0,16)}</text>
            <text x={cx} y={cy+21} textAnchor="middle" fontSize={12} fill={T.text1} fontWeight={900} fontFamily="monospace">({(slices[hov].p*100).toFixed(1)}%)</text>
          </>
        ):(
          <text x={cx} y={cy+13} textAnchor="middle" fontSize={11.5} fill={T.text1} fontWeight={900} fontFamily="monospace">{fmtM(total)}</text>
        )}
      </svg>
      <div style={{display:"flex",flexDirection:"column",gap:5,flex:1,minWidth:0}}>
        {slices.map((s,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 9px",borderRadius:8,
            background:hov===i?`${s.color}14`:"transparent",transition:"all 0.15s",cursor:"default"}}
            onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}>
            <div style={{width:10,height:10,borderRadius:3,background:s.color,flexShrink:0,boxShadow:`0 0 8px ${s.color}80`}}/>
            <span style={{fontSize:10.5,color:T.text2,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.label}</span>
            <span style={{fontSize:11,fontWeight:800,color:T.text1,fontFamily:"monospace"}}>{fmtM(s.value)}</span>
            <span style={{fontSize:9.5,color:s.color,minWidth:36,textAlign:"right",fontWeight:700}}>({(s.p*100).toFixed(1)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DrillRow({label,value,prev,type,revenueType,month,year,pmonth,pyear,T,lower=false}){
  const [open,setOpen]=useState(false);
  const [subData,setSubData]=useState(null);
  const [prevSubData,setPrevSubData]=useState(null);
  const [loading,setLoading]=useState(false);
  const fetchDrill=async()=>{
    if(subData!==null){setOpen(o=>!o);return;}
    setLoading(true);
    try{
      const [data,pdata]=await Promise.all([
        fetch(`${API}/api/drilldown?month=${month}&year=${year}&type=${encodeURIComponent(type)}&revenue_type=${encodeURIComponent(revenueType)}`).then(x=>x.json()),
        pmonth?fetch(`${API}/api/drilldown?month=${pmonth}&year=${pyear}&type=${encodeURIComponent(type)}&revenue_type=${encodeURIComponent(revenueType)}`).then(x=>x.json()):Promise.resolve({}),
      ]);
      setSubData(data);setPrevSubData(pdata);setOpen(true);
    }catch{setSubData({});setPrevSubData({});}
    finally{setLoading(false);}
  };
  const subEntries=subData?Object.entries(subData):[];
  return(
    <div>
      <div style={{display:"flex",alignItems:"center",padding:"10px 18px",gap:10,minHeight:46,
        background:"transparent",transition:"background 0.18s",cursor:"pointer"}}
        onMouseEnter={e=>{e.currentTarget.style.background=T.rowHover;}}
        onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
        <button onClick={fetchDrill} style={{
          width:22,height:22,borderRadius:6,flexShrink:0,
          border:`1px solid ${open?TSI_ORANGE:`rgba(249,115,22,0.4)`}`,
          background:open?`linear-gradient(135deg,${TSI_ORANGE},${TSI_RED})`:"transparent",
          cursor:"pointer",color:open?"#fff":TSI_ORANGE,fontSize:10,fontWeight:900,
          display:"flex",alignItems:"center",justifyContent:"center",
          transition:"all 0.22s cubic-bezier(0.34,1.56,0.64,1)",
          boxShadow:open?`0 3px 10px rgba(249,115,22,0.4)`:"none"}}>
          {loading?"·":open?"▾":"▸"}
        </button>
        <span style={{fontSize:11.5,fontWeight:700,flex:1,color:T.text1,lineHeight:1.3}}>{label}</span>
        <ValRow value={value} prev={prev} lower={lower} T={T}/>
      </div>
      {open&&(
        <div style={{background:dark_ref?"rgba(0,0,0,0.22)":"rgba(99,102,241,0.02)",
          borderTop:`none`,padding:"12px 16px 14px 52px"}}>
          {subEntries.length===0?(
            <p style={{margin:0,color:T.text3,fontSize:11,fontStyle:"italic"}}>No sub-items found</p>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <p style={{margin:"0 0 8px",fontSize:8.5,fontWeight:700,color:TSI_ORANGE,letterSpacing:2,textTransform:"uppercase"}}>
                Account Breakdown · {subEntries.length} items
              </p>
              {subEntries.map(([k,v],i)=>{
                const pv=prevSubData?.[k]??null;
                const chg=pv!=null&&pv!==0?((v-pv)/Math.abs(pv)*100):null;
                const col=DRILL_C[i%DRILL_C.length];
                const isPos = chg!=null ? (lower ? chg<0 : chg>0) : false;
                return(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 12px",borderRadius:9,
                    background:dark_ref?"rgba(255,255,255,0.025)":"rgba(99,102,241,0.025)",
                    border:`1px solid ${col}20`}}>
                    <div style={{width:8,height:8,borderRadius:2,flexShrink:0,background:col}}/>
                    <span style={{fontSize:11,color:T.text2,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{k}</span>
                    {chg!=null&&(
                      <span style={{fontSize:10,fontWeight:700,
                          background:isPos?"rgba(34,197,94,0.13)":"rgba(244,63,94,0.13)",
                          color:isPos?T.green:T.red,
                          borderRadius:20,padding:"2px 8px"}}>
                          {chg>0?"▲":"▼"} {Math.abs(chg).toFixed(2)}%
                        </span>
                    )}
                    <ValBox value={v} T={T} width={92}/>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// function DataRow({label,value,prev,lower=false,fmt=fmtM,isHeader=false,donutData,donutColors,T}){
function DataRow({label,value,prev,lower=false,fmt=fmtM,isHeader=false,donutData,donutColors,T,valueColor=null}){  
return(
    <div style={{display:"flex",alignItems:"center",padding:isHeader?"8px 18px":"10px 18px",
      borderBottom:`none`,gap:0,minHeight:46,
      background:isHeader?`${TSI_ORANGE}0c`:"transparent",transition:"background 0.18s"}}
      onMouseEnter={e=>{if(!isHeader)e.currentTarget.style.background=T.rowHover;}}
      onMouseLeave={e=>{e.currentTarget.style.background=isHeader?`${TSI_ORANGE}0c`:"transparent";}}>
      <span style={{fontSize:isHeader?9.5:11.5,fontWeight:isHeader?900:700,flex:1,
        color:isHeader?TSI_ORANGE:T.text2,textTransform:isHeader?"uppercase":"none",
        letterSpacing:isHeader?1.8:0}}>{label}</span>
      <div style={{width:48,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",marginRight:8}}>
        {donutData&&<MiniDonut data={donutData} colors={donutColors} size={40}/>}
      </div>

      {/* <ValRow value={value} prev={prev} lower={lower} fmt={fmt} T={T}/> */}
      <ValRow value={value} prev={prev} lower={lower} fmt={fmt} T={T} valueColor={valueColor}/>
    </div>
  );
}

function SCard({title,accent,children,T,id,totalValue,totalFmt=fmtM,subtitle=""}){
  return(
    <div id={id} style={{
      background:T.card,border:`1px solid ${T.cardBorder}`,
      borderRadius:18,overflow:"visible",flex:1,minWidth:0,
      scrollMarginTop:"120px",
      boxShadow:dark_ref?`0 4px 40px rgba(0,0,0,0.35),inset 0 1px 0 rgba(255,255,255,0.04)`:`0 4px 28px rgba(99,102,241,0.07)`}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"13px 20px",borderBottom:`none`,
        background:`linear-gradient(110deg,${accent}22 0%,${accent}08 45%,transparent 100%)`,
        borderRadius:"18px 18px 0 0"}}>
        <div style={{display:"flex",alignItems:"center",gap:11}}>
          <div style={{width:4,height:22,background:`linear-gradient(180deg,${accent},${accent}35)`,borderRadius:3}}/>
          <div style={{display:"flex",flexDirection:"column",gap:2}}>
            <span style={{fontSize:12,fontWeight:900,letterSpacing:2.8,textTransform:"uppercase",color:accent}}>{title}</span>
            {subtitle&&(
              <span style={{fontSize:9.5,fontWeight:600,color:accent,opacity:0.6,letterSpacing:0.3,textTransform:"none"}}>
                {subtitle}
              </span>
            )}
          </div>
        </div>
        {totalValue!=null&&(
          <span style={{fontSize:14,fontWeight:900,color:accent,fontFamily:"'JetBrains Mono',Consolas,monospace",
            padding:"3px 14px",borderRadius:10,background:`${accent}12`,border:`1px solid ${accent}28`}}>
            {totalFmt(totalValue)}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function KpiTile({label,value,prev,lower=false,fmt=fmtM,color,T,onClick,icon,showIcon=false,periodLabel="",subLabel=""}){
  const p=momPct(value,prev);
  const good=p!=null&&(lower?p<0:p>0);
  return(
    <div onClick={onClick} style={{
      background:T.card,border:`1px solid ${color}18`,borderRadius:16,padding:"14px 19px",
      flex:1,minWidth:0,position:"relative",overflow:"visible",
      cursor:onClick?"pointer":"default",transition:"all 0.24s cubic-bezier(0.34,1.56,0.64,1)"}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-5px) scale(1.025)";}}
      onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0) scale(1)";}}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${color},${color}70,transparent)`,borderRadius:"16px 16px 0 0"}}/>
      {periodLabel&&(
        <div style={{fontSize:13,fontWeight:900,color:T.text1,letterSpacing:0.5,marginBottom:3,lineHeight:1.2}}>
          {periodLabel}
        </div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={{fontSize:subLabel?10.5:8.5,fontWeight:subLabel?600:700,
          color:subLabel?T.text3:T.text3,
          letterSpacing:subLabel?0.2:2.3,
          textTransform:subLabel?"none":"uppercase"}}>
          {subLabel || label}
        </span>
        {showIcon&&<span style={{fontSize:20}}>{icon}</span>}
      </div>
      <div style={{fontSize:25,fontWeight:900,color:T.text1,fontFamily:"'JetBrains Mono',Consolas,monospace",lineHeight:1,marginBottom:10}}>
        <Counter val={value} fmt={fmt}/>
      </div>
      {p!=null&&(
        <span style={{fontSize:10,fontWeight:700,display:"inline-flex",alignItems:"center",gap:3,
          color:good?T.green:T.red,
          background:good?`rgba(34,197,94,0.13)`:`rgba(244,63,94,0.13)`,
          border:`1px solid ${good?`rgba(34,197,94,0.3)`:`rgba(244,63,94,0.3)`}`,
          padding:"3px 9px",borderRadius:20}}>
          {p>0?"▲":"▼"} {Math.abs(p).toFixed(2)}%
        </span>
      )}
    </div>
  );
}

function NavItem({icon,label,onClick,T,collapsed,isActive}){
  const [hov,setHov]=useState(false);
  return(
    <div onClick={onClick} title={collapsed?label:""}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{display:"flex",alignItems:"center",gap:collapsed?0:11,padding:collapsed?"11px 0":"9px 13px",
        justifyContent:collapsed?"center":"flex-start",borderRadius:11,cursor:"pointer",
        marginBottom:3,background:isActive?"linear-gradient(90deg,rgba(249,115,22,0.22),rgba(249,115,22,0.09),rgba(249,115,22,0.02))":hov?"rgba(255,255,255,0.07)":"transparent",
        borderLeft:isActive?`3px solid ${TSI_ORANGE}`:"3px solid transparent",
        transition:"all 0.24s cubic-bezier(0.34,1.56,0.64,1)"}}>
      {icon&&<span style={{fontSize:19,flexShrink:0,lineHeight:1}}>{icon}</span>}
      {!collapsed&&(<span style={{fontSize:13,fontWeight:isActive?800:700,color:isActive?TSI_ORANGE:hov?"#e2e8f0":"#94a3b8"}}>{label}</span>)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ═══ SESSION 10: TRENDS — METRICS, SCALES, AXIS SYSTEM ═══════════
// ══════════════════════════════════════════════════════════════════

const TREND_METRICS = [
  {name:"Net Revenue",       key:"netRevenue",   color:"#ea580c", grp:"dollar", fmt:v=>v>=1_000_000?`$${(v/1_000_000).toFixed(2)}M`:v>=1000?`$${(v/1000).toFixed(2)}K`:`$${Math.round(v)}`},
  {name:"Net Rev/Employee",  key:"netRevPerEmp", color:"#0e7490", grp:"dollar", fmt:v=>`$${Math.round(v).toLocaleString()}`},
  {name:"OPEX",              key:"opex",         color:"#1d4ed8", grp:"dollar", fmt:v=>v>=1_000_000?`$${(v/1_000_000).toFixed(2)}M`:v>=1000?`$${(v/1000).toFixed(2)}K`:`$${Math.round(v)}`},
  {name:"Salaries+Benefits", key:"salBen",       color:"#b45309", grp:"dollar", fmt:v=>v>=1_000_000?`$${(v/1_000_000).toFixed(2)}M`:v>=1000?`$${(v/1000).toFixed(2)}K`:`$${Math.round(v)}`},
  {name:"Cost/Employee",     key:"costPerEmp",   color:"#be185d", grp:"dollar", fmt:v=>`$${Math.round(v).toLocaleString()}`},
  {name:"Head Count",        key:"headCount",    color:"#7e22ce", grp:"count",  fmt:v=>`${Math.round(v)}`},
  {name:"EBITA %",           key:"ebitaPct",     color:"#15803d", grp:"pct",    fmt:v=>`${Number(v).toFixed(2)}%`},
  {name:"G&A Ratio",         key:"gaRatio",      color:"#0f766e", grp:"pct",    fmt:v=>`${Number(v).toFixed(2)}%`},
  {name:"PS Ratio",          key:"psRatio",      color:"#32e60e", grp:"ratio",  fmt:v=>`${Number(v).toFixed(2)}`},
  {name:"Maintenance Ratio", key:"maRatio",      color:"#b91c1c", grp:"ratio",  fmt:v=>`${Number(v).toFixed(2)}`},
  {name:"R&D Ratio",         key:"rdRatio",      color:"#3730a3", grp:"ratio",  fmt:v=>`${Number(v).toFixed(2)}`},
  {name:"S&M Ratio",         key:"smRatio",      color:"#ca8a04", grp:"ratio",  fmt:v=>`${Number(v).toFixed(2)}`},
];

const CHART_META = {
  combo:     { title:"Trend Analysis — Combo",    xLabel:"Monthly Performance Period" },
  lines:     { title:"Trend Analysis — All Lines", xLabel:"Monthly Performance Period" },
  bars:      { title:"Trend Analysis — All Bars",  xLabel:"Reporting Month" },
  waterfall: { title:"Period-over-Period Change",   xLabel:"Reporting Month" },
};

const SCALE_PRIORITY = ["dollar","pct","ratio","count"];

function getDollarRange(metrics, trendData) {
  const vals = metrics.flatMap(m => trendData.map(d => d[m.key] ?? 0));
  const mx = Math.max(...vals.map(Math.abs), 1);
  const mag = Math.pow(10, Math.floor(Math.log10(mx)));
  const top = Math.ceil(mx / mag) * mag;
  return { min: -top, max: top };
}

function getScaleRange(grp, metrics, trendData) {
  if (grp === "dollar") return getDollarRange(metrics, trendData);
  if (grp === "pct")    return { min: -50, max: 50 };
  if (grp === "ratio")  return { min: -20, max: 20 };
  if (grp === "count")  return { min: 0,   max: 350 };
  return { min: 0, max: 100 };
}

function getScaleColor(grp) {
  return { dollar:"#60a5fa", pct:"#86efac", ratio:"#f59e0b", count:"#a855f7" }[grp] || "#e2e8f0";
}

function getScaleTitle(grp) {
  return { dollar:"Financial Value ($)", pct:"Percentage (%)", ratio:"Ratio", count:"Head Count" }[grp] || "Value";
}

function fmtScaleTick(v, grp) {
  if (grp === "dollar") {
    const a=Math.abs(v), s=v<0?"-":"";
    if(a>=1e3) return s+"$"+Math.round(a/1e3).toLocaleString()+"K";
    return s+"$"+Math.round(a);
  }
  if (grp === "pct")   return (v>0?"+":"")+v.toFixed(0)+"%";
  if (grp === "ratio") return (v>0?"+":"")+v.toFixed(0);
  if (grp === "count") return Math.round(v).toString();
  return String(v);
}

function normRatioToPct(v) { return ((v + 20) / 40) * 100 - 50; }

function assignAxes(selMetrics) {
  const groups = {};
  selMetrics.forEach(m => { if (!groups[m.grp]) groups[m.grp] = []; groups[m.grp].push(m); });
  const sorted = SCALE_PRIORITY.filter(g => groups[g]);
  if (sorted.length === 0) return { left: null, right: null, drawnAxes: [] };
  const left  = { grp: sorted[0], metrics: groups[sorted[0]] };
  const right = sorted.length >= 2 ? { grp: sorted[1], metrics: groups[sorted[1]] } : null;
  const drawnAxes = sorted.slice(2).map(g => ({ grp: g, metrics: groups[g] }));
  return { left, right, drawnAxes };
}

// ══════════════════════════════════════════════════════════════════
// ═══ SESSION 10: TrendsCard COMPONENT ════════════════════════════
// ══════════════════════════════════════════════════════════════════

function TrendsCard({month, year, T}){
  const TREND_INDIGO = "#6366f1";

  const [is6M,       setIs6M]       = useState(true);
  const [chartMode,  setChartMode]  = useState("combo");
  const [panelOpen,  setPanelOpen]  = useState(true);
  const [panelW,     setPanelW]     = useState(220);
  const [dragging,   setDragging]   = useState(false);
  const [selected,   setSelected]   = useState(new Set(["Net Revenue","EBITA %"]));
  const [trendData,  setTrendData]  = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [chartReady, setChartReady] = useState(!!window.Chart);

  const canvasRef  = useRef(null);
  const chartRef   = useRef(null);
  const dragStartX = useRef(0);
  const dragStartW = useRef(220);

  useEffect(()=>{
    if(window.Chart){ setChartReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
    s.onload = ()=>setChartReady(true);
    document.head.appendChild(s);
  },[]);

  useEffect(()=>{
    if(!dragging) return;
    const onMove = e=>{
      const dx = e.clientX - dragStartX.current;
      const nw = Math.max(160, Math.min(340, dragStartW.current + dx));
      setPanelW(nw);
    };
    const onUp = ()=>setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); };
  },[dragging]);

  const startDrag = e=>{
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartW.current = panelW;
    setDragging(true);
  };

  const buildMonthList = useCallback((m, y, n)=>{
    const idx = MONTHS.indexOf(m.toUpperCase());
    const list = [];
    for(let i = n-1; i >= 0; i--){
      let mIdx = idx - i;
      let yr   = y;
      if(mIdx < 0){ mIdx += 12; yr = y - 1; }
      list.push({month: MONTHS[mIdx], year: yr});
    }
    return list;
  },[]);

  // ── fetchTrends — v6.5: gaRatio/smRatio ×100 ──
  const fetchTrends = useCallback(async()=>{
    setLoading(true);
    try {
      const n     = is6M ? 6 : 3;
      const mList = buildMonthList(month, year, n);
      const results = await Promise.all(
        mList.map(async({month:m, year:y})=>{
          const [pnl,eff_,rat,exp,opexBreak] = await Promise.all([
            fetch(`${API}/api/pnl?month=${m}&year=${y}`).then(x=>x.json()).catch(()=>({})),
            fetch(`${API}/api/efficiency?month=${m}&year=${y}`).then(x=>x.json()).catch(()=>({})),
            fetch(`${API}/api/ratios?month=${m}&year=${y}`).then(x=>x.json()).catch(()=>({})),
            fetch(`${API}/api/expense-items?month=${m}&year=${y}`).then(x=>x.json()).catch(()=>({})),
            fetch(`${API}/api/opex-breakdown?month=${m}&year=${y}`).then(x=>x.json()).catch(()=>({})),
          ]);
          const exp_ = {...exp, gaOpex: opexBreak?.["General & Administration"]??0, smOpex: opexBreak?.["Sales & Marketing"]??0};
          const gr  = pnl?.grossRevenue ?? 0;
          const cg  = pnl?.cogs ?? 0;
          const nr  = gr - cg;
          const hc  = pnl?.headCount ?? 1;
          const op  = pnl?.opex ?? 0;
          const eb  = pnl?.ebitaPct ?? (nr>0 ? ((nr-op)/nr*100) : 0);
          const sal = (exp_?.salaries??0) + (exp_?.benefits??0);
          const rat_= rat ?? {};
          const getR= key=>{ const v=rat_[key]; if(v==null) return 0; if(typeof v==="object") return v.display??0; return v; };
          return {
            label:        `${m.slice(0,3)} ${String(y).slice(2)}`,
            netRevenue:   nr,
            netRevPerEmp: eff_?.netRevPerEmp ?? (hc>0 ? Math.round(nr/hc) : 0),
            opex:         op,
            headCount:    hc,
            ebitaPct:     eb,
            salBen:       sal,
            costPerEmp:   eff_?.costPerEmp   ?? (hc>0 ? Math.round(op/hc) : 0),
            psRatio:      getR("Professional Services (>2)"),
            maRatio:      getR("Maintenance (>4)"),
            rdRatio:      getR("R&D (>1)"),
            // gaRatio:      nr>0 ? ((exp_?.gaOpex??0)/nr)*100 : 0,
            gaRatio:      getR("Administration (<15%)"),
            // smRatio:      nr>0 ? ((exp_?.smOpex??0)/nr)*100 : 0,
            smRatio:      getR("Sales & Marketing (>1.5)"),
          };
        })
      );
      setTrendData(results);
    } catch(e){ console.error(e); }
    finally { setLoading(false); }
  },[month, year, is6M, buildMonthList]);

  useEffect(()=>{ fetchTrends(); },[fetchTrends]);

  // ══════════════════════════════════════════════════════════════
  // BUILD CHART — Session 10 core: 4 scales, smart axis, 4 modes
  // ══════════════════════════════════════════════════════════════
  useEffect(()=>{
    if(!chartReady||!canvasRef.current||trendData.length===0) return;
    if(chartRef.current){ chartRef.current.destroy(); chartRef.current=null; }

    const Chart = window.Chart;
    if(!Chart) return;

    const selMetrics = TREND_METRICS.filter(m=>selected.has(m.name));
    if(selMetrics.length===0) return;

    const labels  = trendData.map(d=>d.label);
    const isDark  = dark_ref;
    const bgColor = isDark?'#0a1228':'#ffffff';
    const labelColor = isDark?'#ffffff':'#000000';
    const gridColor  = isDark?'rgba(255,255,255,0.12)':'rgba(0,0,0,0.08)';

    Chart.defaults.font.weight = 'bold';
    Chart.defaults.font.size   = 11;
    Chart.defaults.color       = labelColor;

    const meta = CHART_META[chartMode] || CHART_META.combo;
    const getData = m => trendData.map(d=>d[m.key]??0);

    // ── Axis assignment ──
    const { left, right, drawnAxes } = assignAxes(selMetrics);
    const leftRange  = left  ? getScaleRange(left.grp, left.metrics, trendData) : {min:0,max:100};
    const rightRange = right ? getScaleRange(right.grp, right.metrics, trendData) : null;

    const ratioDrawn = drawnAxes.find(a => a.grp === "ratio");
    const countDrawn = drawnAxes.find(a => a.grp === "count");
    const pctOnRight = right && right.grp === "pct";
    const normaliseRatio = ratioDrawn && pctOnRight;
    const hasRight = right != null;

    // ═════════════════════════════════════════════════════════
    // WATERFALL
    // ═════════════════════════════════════════════════════════
    if(chartMode==="waterfall"){
      const m0 = selMetrics[0];
      const raw = getData(m0);
      const deltas = raw.map((v,i) => i===0 ? 0 : +((v-raw[i-1])/Math.abs(raw[i-1]||1)*100).toFixed(2));
      const colors = deltas.map(v => v>0?'#22c55e':v<0?'#ef4444':'rgba(148,163,184,0.6)');
      const mx = Math.max(...deltas.map(Math.abs), 1);
      const pad = mx * 0.25;

      chartRef.current = new Chart(canvasRef.current, {
        type:'bar',
        data:{ labels, datasets:[{ label:m0.name, data:deltas, backgroundColor:colors, borderColor:colors, borderWidth:0, borderRadius:6, barPercentage:0.5, categoryPercentage:0.65 }] },
        options:{
          responsive:true, maintainAspectRatio:false,
          interaction:{mode:'index',intersect:false},
          plugins:{
            legend:{display:false},
            title:{display:true, text:meta.title, color:labelColor, font:{size:14,weight:'bold'}, padding:{bottom:12}, align:'center'},
            tooltip:{
              backgroundColor:isDark?'#1e2a40':'#1e293b', borderColor:'rgba(255,255,255,0.1)', borderWidth:1,
              titleColor:'#fff', bodyColor:'#e2e8f0', titleFont:{weight:'bold',size:12}, bodyFont:{weight:'bold',size:11}, padding:12,
              callbacks:{ label:ctx=>` ${m0.name}: ${ctx.parsed.y>0?'+':''}${ctx.parsed.y.toFixed(2)}%` }
            }
          },
          scales:{
            x:{ ticks:{color:labelColor,font:{size:11,weight:'bold'},maxRotation:0}, grid:{display:false}, border:{display:false},
              title:{display:true,text:meta.xLabel,color:labelColor,font:{size:11,weight:'bold'},padding:{top:8}} },
            y:{ min:-(mx+pad), max:mx+pad,
              ticks:{color:labelColor,font:{size:11,weight:'bold'},callback:v=>(v>0?'+':'')+v.toFixed(0)+'%'},
              grid:{display:true,color:gridColor,lineWidth:1,drawTicks:false}, border:{display:false},
              title:{display:true,text:'Month-over-Month Change (%)',color:labelColor,font:{size:11,weight:'bold'},padding:{bottom:6}} },
          }
        },
        plugins:[{
          id:'zeroLine',
          afterDraw(chart){
            const {ctx,chartArea,scales:sc}=chart;
            const yZ=sc.y.getPixelForValue(0);
            if(yZ>=chartArea.top&&yZ<=chartArea.bottom){
              ctx.save();ctx.beginPath();ctx.moveTo(chartArea.left,yZ);ctx.lineTo(chartArea.right,yZ);
              ctx.strokeStyle='rgba(255,255,255,0.22)';ctx.lineWidth=1.2;ctx.setLineDash([5,4]);ctx.stroke();ctx.restore();
            }
          }
        }]
      });
      return;
    }

    // ═════════════════════════════════════════════════════════
    // COMBO / ALL LINES / ALL BARS
    // ═════════════════════════════════════════════════════════
    const getAxisId = m => {
      if(left && left.metrics.includes(m)) return 'y';
      if(right && right.metrics.includes(m)) return 'y2';
      if(normaliseRatio && ratioDrawn && ratioDrawn.metrics.includes(m)) return 'y2';
      return 'y';
    };

    const getPlotData = m => {
      const raw = getData(m);
      if(normaliseRatio && ratioDrawn && ratioDrawn.metrics.includes(m)) return raw.map(normRatioToPct);
      return raw;
    };

    const isBarMetric = m => {
      if(chartMode === 'lines') return false;
      if(chartMode === 'bars') return true;
      return m.grp === 'dollar' || m.grp === 'count';
    };

    const isDrawnOnly = m => countDrawn && countDrawn.metrics.includes(m);

    const datasets = selMetrics.filter(m => !isDrawnOnly(m)).map(m => {
      const raw = getData(m);
      const plotData = getPlotData(m);
      const bar = isBarMetric(m);
      return {
        type: bar?'bar':'line',
        label: m.name, data: plotData,
        borderColor: m.color,
        backgroundColor: bar ? raw.map(v=>v<0?'#ef4444':m.color+'80') : 'transparent',
        segment: { borderColor: ctx => { try { return ctx.p1.parsed.y<0?'#ef4444':m.color; } catch{ return m.color; } } },
        borderRadius: bar?5:undefined,
        barPercentage: bar?0.55:undefined,
        categoryPercentage: bar?0.72:undefined,
        pointRadius: bar?0:6,
        pointBackgroundColor: raw.map(v=>v<0?'#ef4444':m.color),
        pointBorderColor: bgColor, pointBorderWidth: bar?0:2,
        borderWidth: bar?0:3,
        tension: bar?undefined:0.4,
        fill: false,
        yAxisID: getAxisId(m),
        order: bar?1:0,
      };
    });

    const hcDatasets = countDrawn ? countDrawn.metrics.map(m => ({
      type:'bar', label:m.name, data:getData(m),
      backgroundColor:m.color+'70', borderColor:m.color,
      borderWidth:0, borderRadius:4, barPercentage:0.3, categoryPercentage:0.72,
      yAxisID:'y3', order:2,
    })) : [];

    // ── Scales ──
    const scales = {
      x: {
        ticks:{color:labelColor,font:{size:11,weight:'bold'},maxRotation:0},
        grid:{display:false}, border:{display:false},
        title:{display:true,text:meta.xLabel,color:labelColor,font:{size:11,weight:'bold'},padding:{top:8}}
      },
      y: {
        position:'left',
        min:leftRange.min, max:leftRange.max,
        ticks:{color:getScaleColor(left.grp),font:{size:11,weight:'bold'},callback:v=>fmtScaleTick(v,left.grp)},
        grid:{display:true,color:gridColor,lineWidth:1,drawTicks:false},
        border:{display:false},
        title:{display:true,text:getScaleTitle(left.grp),color:getScaleColor(left.grp),font:{size:11,weight:'bold'},padding:{bottom:6}}
      },
      y2: {
        position:'right',
        display:hasRight,
        min:rightRange?rightRange.min:undefined, max:rightRange?rightRange.max:undefined,
        ticks:{display:hasRight,color:right?getScaleColor(right.grp):'#fff',font:{size:11,weight:'bold'},
          callback:v=>right?fmtScaleTick(v,right.grp):''},
        grid:{display:false}, border:{display:false},
        title:{display:hasRight,text:right?getScaleTitle(right.grp):'',color:right?getScaleColor(right.grp):'#fff',font:{size:11,weight:'bold'},padding:{bottom:6}}
      },
    };

    if(countDrawn){
      scales.y3 = { position:'right', display:false, min:0, max:350, grid:{display:false}, border:{display:false}, ticks:{display:false} };
    }

    // ── Plugins ──
    const plugins = [];

    // Zero line — y and y2 when crossing zero
    plugins.push({
      id:'zeroLine',
      afterDraw(chart){
        const {ctx,chartArea,scales:sc}=chart;
        ['y','y2'].forEach(id=>{
          const axis=sc[id]; if(!axis||!axis.options.display) return;
          if(axis.min>=0||axis.max<=0) return;
          const yZ=axis.getPixelForValue(0);
          if(yZ<chartArea.top||yZ>chartArea.bottom) return;
          ctx.save();ctx.beginPath();ctx.moveTo(chartArea.left,yZ);ctx.lineTo(chartArea.right,yZ);
          ctx.strokeStyle=isDark?'rgba(255,255,255,0.22)':'rgba(0,0,0,0.18)';ctx.lineWidth=1.2;ctx.setLineDash([5,4]);ctx.stroke();ctx.restore();
        });
      }
    });

    // HC drawn axis — numbers only, NO "ppl"
    if(countDrawn){
      plugins.push({
        id:'hcAxis',
        afterDraw(chart){
          const {ctx,chartArea}=chart;
          const {top,bottom,right:r}=chartArea;
          const AX = r + (hasRight ? 68 : 12);
          const hcTicks = [0, 100, 200, 300, 350];
          ctx.save();
          ctx.beginPath();ctx.strokeStyle='rgba(168,85,247,0.7)';ctx.lineWidth=1;
          ctx.moveTo(AX,top);ctx.lineTo(AX,bottom);ctx.stroke();
          ctx.font='bold 9px system-ui,sans-serif';ctx.fillStyle='#a855f7';ctx.textAlign='left';
          hcTicks.forEach(hv=>{
            const yPos = bottom - ((hv/350)*(bottom-top));
            if(yPos<top-2||yPos>bottom+2) return;
            ctx.beginPath();ctx.strokeStyle='rgba(168,85,247,0.8)';ctx.lineWidth=1;
            ctx.moveTo(AX,yPos);ctx.lineTo(AX+4,yPos);ctx.stroke();
            ctx.fillText(String(hv), AX+7, yPos+3);
          });
          ctx.font='bold 9px system-ui,sans-serif';ctx.fillStyle='#a855f7';ctx.textAlign='left';
          ctx.fillText('HC', AX+2, top-6);
          ctx.restore();
        }
      });
    }

    // Ratio drawn axis — when BOTH ratio AND pct on right
    if(normaliseRatio && ratioDrawn){
      plugins.push({
        id:'ratioAxis',
        afterDraw(chart){
          const {ctx,chartArea}=chart;
          const {top,bottom,right:r}=chartArea;
          const offset = countDrawn ? 120 : 68;
          const AX = r + offset;
          const ratioTicks = [-20, -10, 0, 10, 20];
          ctx.save();
          ctx.beginPath();ctx.strokeStyle='rgba(245,158,11,0.7)';ctx.lineWidth=1;
          ctx.moveTo(AX,top);ctx.lineTo(AX,bottom);ctx.stroke();
          ctx.font='bold 9px system-ui,sans-serif';ctx.fillStyle='#f59e0b';ctx.textAlign='left';
          const y2Axis = chart.scales.y2;
          ratioTicks.forEach(rv=>{
            const normV = normRatioToPct(rv);
            const yPos = y2Axis ? y2Axis.getPixelForValue(normV) : bottom-(((rv+20)/40)*(bottom-top));
            if(yPos<top-2||yPos>bottom+2) return;
            ctx.beginPath();ctx.strokeStyle='rgba(245,158,11,0.8)';ctx.lineWidth=1;
            ctx.moveTo(AX,yPos);ctx.lineTo(AX+4,yPos);ctx.stroke();
            ctx.fillText((rv>0?'+':'')+rv, AX+7, yPos+3);
          });
          ctx.font='bold 9px system-ui,sans-serif';ctx.fillStyle='#f59e0b';ctx.textAlign='left';
          ctx.fillText('Ratio', AX+2, top-6);
          ctx.restore();
        }
      });
    }

    const rightPad = (countDrawn ? 60 : 0) + (normaliseRatio && ratioDrawn ? 60 : 0);

    // ── Tooltip: always actual raw values ──
    const tooltipCallbacks = {
      label: ctx => {
        const mainCount = selMetrics.filter(m => !isDrawnOnly(m)).length;
        let m;
        if(ctx.datasetIndex < mainCount){
          const nonDrawn = selMetrics.filter(mm => !isDrawnOnly(mm));
          m = nonDrawn[ctx.datasetIndex];
        } else {
          const hcIdx = ctx.datasetIndex - mainCount;
          m = countDrawn ? countDrawn.metrics[hcIdx] : null;
        }
        if(!m) return '';
        const raw = trendData.map(d => d[m.key] ?? 0);
        return ` ${m.name}: ${m.fmt(raw[ctx.dataIndex])}`;
      }
    };

    chartRef.current = new Chart(canvasRef.current, {
      data: { labels, datasets: [...datasets, ...hcDatasets] },
      options: {
        responsive:true, maintainAspectRatio:false,
        layout: { padding: { right: rightPad } },
        interaction: { mode:'index', intersect:false },
        plugins: {
          legend: { display:false },
          title: { display:true, text:meta.title, color:labelColor, font:{size:14,weight:'bold'}, padding:{bottom:12}, align:'center' },
          tooltip: {
            backgroundColor:isDark?'#1e2a40':'#1e293b', borderColor:'rgba(255,255,255,0.1)', borderWidth:1,
            titleColor:'#fff', bodyColor:'#e2e8f0', titleFont:{weight:'bold',size:12}, bodyFont:{weight:'bold',size:11}, padding:12,
            callbacks: tooltipCallbacks
          }
        },
        scales
      },
      plugins
    });

  },[trendData, selected, chartReady, chartMode, panelOpen, T]);

  const toggleMetric = name=>{
    setSelected(prev=>{
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const selMetrics = TREND_METRICS.filter(m=>selected.has(m.name));

  const periodLabel = (()=>{
    const n    = is6M ? 6 : 3;
    const list = buildMonthList(month, year, n);
    const first = list[0];
    const last  = list[list.length-1];
    const fy = String(first.year).slice(2);
    const ly = String(last.year).slice(2);
    return `${first.month.slice(0,3)} ${fy} → ${last.month.slice(0,3)} ${ly}`;
  })();

  // ── TrendsCard RENDER ──
  return(
    <div id="section-trends" style={{
      background:T.card, border:`1px solid ${T.cardBorder}`,
      borderRadius:18, overflow:"hidden",
      scrollMarginTop:"120px",
      boxShadow:dark_ref
        ?`0 4px 40px rgba(0,0,0,0.35),inset 0 1px 0 rgba(255,255,255,0.04)`
        :`0 4px 28px rgba(99,102,241,0.07)`
    }}>

      {/* ── Card header ── */}
      <div style={{
        display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"13px 20px",
        background:`linear-gradient(110deg,${TREND_INDIGO}1c 0%,${TREND_INDIGO}07 50%,transparent 100%)`,
        borderBottom:`1px solid ${T.divider}`
      }}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:3,height:20,background:TREND_INDIGO,borderRadius:2,flexShrink:0}}/>
          <span style={{fontSize:11,fontWeight:700,letterSpacing:2.5,textTransform:"uppercase",color:TREND_INDIGO}}>TRENDS</span>
          <span style={{
            fontSize:9,fontWeight:600,padding:"3px 10px",borderRadius:20,
            background:is6M?"rgba(245,158,11,0.1)":`${TREND_INDIGO}12`,
            color:is6M?"#f59e0b":TREND_INDIGO,
            border:`0.5px solid ${is6M?"rgba(245,158,11,0.3)":`${TREND_INDIGO}30`}`
          }}>{periodLabel}</span>
        </div>

        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {/* v6.5: 4 SVG icon mode buttons */}
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{display:"flex",gap:2,background:dark_ref?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)",
              borderRadius:8,padding:3}}>
              {[
                {id:"combo", title:"Combo", svg:<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="9" width="3" height="6" rx="1" fill="currentColor" opacity="0.9"/><rect x="6" y="6" width="3" height="9" rx="1" fill="currentColor" opacity="0.9"/><rect x="11" y="4" width="3" height="11" rx="1" fill="currentColor" opacity="0.5"/><path d="M2.5 7 C5 4 8 3 12.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>},
                {id:"lines", title:"All Lines", svg:<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 13 C4 13 5 3 8 5 C11 7 13 3 15 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none"/><circle cx="1" cy="13" r="2" fill="currentColor"/><circle cx="8" cy="5" r="2" fill="currentColor"/><circle cx="15" cy="3" r="2" fill="currentColor"/></svg>},
                {id:"bars", title:"All Bars", svg:<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="7" width="3" height="8" rx="1" fill="currentColor" opacity="0.85"/><rect x="5" y="4" width="3" height="11" rx="1" fill="currentColor" opacity="0.65"/><rect x="9" y="9" width="3" height="6" rx="1" fill="currentColor" opacity="0.85"/><rect x="13" y="6" width="3" height="9" rx="1" fill="currentColor" opacity="0.65"/></svg>},
                {id:"waterfall", title:"Waterfall", svg:<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="4" width="3" height="5" rx="1" fill="currentColor" opacity="0.7"/><rect x="5" y="8" width="3" height="4" rx="1" fill="currentColor" opacity="0.5"/><rect x="9" y="3" width="3" height="7" rx="1" fill="currentColor" opacity="0.7"/><rect x="13" y="6" width="3" height="5" rx="1" fill="currentColor" opacity="0.5"/><path d="M2.5 4 L6.5 8 M8 8 L10.5 3 M12 3 L14.5 6" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeDasharray="1.5 1" opacity="0.5"/></svg>},
              ].map(({id,title,svg})=>{
                const active = chartMode===id;
                return(
                  <button key={id} onClick={()=>setChartMode(id)} title={title}
                    style={{
                      width:30,height:26,borderRadius:6,border:"none",cursor:"pointer",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      transition:"all 0.18s",
                      background:active?TREND_INDIGO:"transparent",
                      color:active?"#fff":T.text3,
                    }}>
                    {svg}
                  </button>
                );
              })}
            </div>
            <span style={{fontSize:10,color:T.text3,fontWeight:700,whiteSpace:"nowrap"}}>
              {{"combo":"Combo","lines":"Lines","bars":"Bars","waterfall":"Δ%"}[chartMode]}
            </span>
          </div>

          {/* 3M / 6M toggle */}
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <span style={{fontSize:11,fontWeight:800,color:is6M?T.text3:TREND_INDIGO,transition:"color 0.2s"}}>3M</span>
            <div onClick={()=>setIs6M(v=>!v)} style={{
              width:40,height:22,borderRadius:11,cursor:"pointer",position:"relative",
              background:is6M?"rgba(245,158,11,0.18)":"rgba(99,102,241,0.18)",
              border:`0.5px solid ${is6M?"rgba(245,158,11,0.4)":"rgba(99,102,241,0.4)"}`,
              transition:"all 0.3s"}}>
              <div style={{
                position:"absolute",top:3,left:is6M?20:3,
                width:16,height:16,borderRadius:"50%",
                background:is6M?"#f59e0b":TREND_INDIGO,
                transition:"left 0.26s cubic-bezier(0.34,1.56,0.64,1)",
                boxShadow:"0 1px 3px rgba(0,0,0,0.25)"}}/>
            </div>
            <span style={{fontSize:11,fontWeight:800,color:is6M?"#f59e0b":T.text3,transition:"color 0.2s"}}>6M</span>
          </div>
        </div>
      </div>

      {/* ── Card body ── */}
      <div style={{display:"flex",minHeight:420}}>

        {/* LEFT: metrics panel */}
        {panelOpen&&(
          <div style={{
            width:panelW,flexShrink:0,
            borderRight:`1px solid ${T.divider}`,
            background:dark_ref?"rgba(0,0,0,0.12)":"rgba(99,102,241,0.012)",
            display:"flex",flexDirection:"column",
            overflow:"hidden",position:"relative",
          }}>
            <div style={{
              display:"flex",justifyContent:"space-between",alignItems:"center",
              padding:"10px 14px 8px",
              borderBottom:`1px solid ${T.divider}`
            }}>
              <span style={{fontSize:9,fontWeight:800,color:T.text3,letterSpacing:1.5,textTransform:"uppercase"}}>
                Metrics
                <span style={{
                  marginLeft:6,color:TREND_INDIGO,fontWeight:800,
                  background:`${TREND_INDIGO}15`,padding:"1px 6px",borderRadius:10,fontSize:9
                }}>({selected.size})</span>
              </span>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <button onClick={()=>setSelected(new Set())}
                  style={{fontSize:10,color:TREND_INDIGO,background:"none",border:"none",
                    cursor:"pointer",padding:0,fontWeight:800,
                    opacity:selected.size>0?1:0.25,transition:"opacity 0.15s"}}>
                  Clear
                </button>
                <button onClick={()=>setPanelOpen(false)}
                  title="Collapse metrics panel"
                  style={{
                    width:26,height:26,borderRadius:8,
                    border:"1px solid rgba(255,255,255,0.1)",
                    background:"rgba(255,255,255,0.04)",
                    cursor:"pointer",color:"rgba(255,255,255,0.5)",
                    fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",
                    flexShrink:0,transition:"all 0.18s"
                  }}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                  onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}>
                  ‹
                </button>
              </div>
            </div>

            <div style={{flex:1,overflowY:"auto"}}>
              {TREND_METRICS.map(m=>{
                const isOn = selected.has(m.name);
                const last = trendData.length>0 ? trendData[trendData.length-1][m.key]??0 : null;
                return(
                  <div key={m.name} onClick={()=>toggleMetric(m.name)}
                    style={{
                      display:"flex",alignItems:"center",gap:9,
                      padding:"9px 14px",cursor:"pointer",
                      background:isOn?`${m.color}0d`:"transparent",
                      borderLeft:`3px solid ${isOn?m.color:"transparent"}`,
                      transition:"all 0.15s",
                      opacity:isOn?1:0.7,
                    }}
                    onMouseEnter={e=>{ e.currentTarget.style.opacity="1"; if(!isOn) e.currentTarget.style.background=T.rowHover; }}
                    onMouseLeave={e=>{ e.currentTarget.style.opacity=isOn?"1":"0.45"; e.currentTarget.style.background=isOn?`${m.color}0d`:"transparent"; }}>
                    <div style={{
                      width:14,height:14,borderRadius:3,flexShrink:0,
                      background:isOn?m.color:"transparent",
                      border:`1.5px solid ${isOn?m.color:"rgba(148,163,184,0.3)"}`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      transition:"all 0.15s"
                    }}>
                      {isOn&&<span style={{color:"#fff",fontSize:9,fontWeight:900,lineHeight:1}}>✓</span>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{
                        margin:0,fontSize:12,fontWeight:800,
                        color:isOn?m.color:T.text1,
                        whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"
                      }}>{m.name}</p>
                      {last!=null&&(
                        <p style={{margin:"1px 0 0",fontSize:11,fontWeight:700,color:T.text2,fontFamily:"'JetBrains Mono',monospace"}}>
                          {m.fmt(last)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{
              padding:"6px 14px",
              borderTop:`1px solid ${T.divider}`,
              fontSize:9,fontWeight:700,color:T.text3,
              display:"flex",justifyContent:"space-between",alignItems:"center"
            }}>
              <span>Drag edge to resize</span>
              <span style={{color:TREND_INDIGO,cursor:"pointer",fontWeight:800}}
                onClick={()=>setSelected(new Set(TREND_METRICS.map(m=>m.name)))}>
                All
              </span>
            </div>

            <div onMouseDown={startDrag}
              style={{
                position:"absolute",top:0,right:0,width:8,height:"100%",
                cursor:"col-resize",zIndex:10,
                display:"flex",alignItems:"center",justifyContent:"center",
                background:dragging?"rgba(99,102,241,0.15)":"transparent",
                transition:"background 0.15s"
              }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(99,102,241,0.12)"}
              onMouseLeave={e=>{ if(!dragging) e.currentTarget.style.background="transparent"; }}>
              <div style={{display:"flex",flexDirection:"column",gap:3,pointerEvents:"none"}}>
                {[0,1,2].map(i=>(
                  <div key={i} style={{
                    width:3,height:3,borderRadius:"50%",
                    background:dragging?TREND_INDIGO:"rgba(148,163,184,0.5)"
                  }}/>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* RIGHT: chart area */}
        <div style={{flex:1,minWidth:0,padding:"14px 18px",display:"flex",flexDirection:"column",gap:10}}>

          {/* Legend — name + value + ▲▼% (last vs prior month) */}
          <div style={{display:"flex",flexWrap:"wrap",gap:8,alignItems:"center",minHeight:20}}>
            {selMetrics.map(m=>{
              const last  = trendData.length>0 ? trendData[trendData.length-1][m.key]??0 : null;
              const prior = trendData.length>1 ? trendData[trendData.length-2][m.key]??0 : null;
              const chg   = (prior!=null&&prior!==0) ? ((last-prior)/Math.abs(prior)*100) : null;
              const good  = chg!=null && chg>0;
              return(
                <span key={m.name} style={{display:"flex",alignItems:"center",gap:5,
                  padding:"3px 9px",borderRadius:6,
                  background:m.color+'14',border:`0.5px solid ${m.color}30`}}>
                  <span style={{width:9,height:9,borderRadius:2,background:m.color,flexShrink:0,display:"inline-block"}}/>
                  <span style={{fontSize:11,fontWeight:800,color:m.color}}>{m.name}</span>
                  {last!=null&&<span style={{fontSize:10,fontWeight:700,color:T.text1,fontFamily:"monospace"}}>{m.fmt(last)}</span>}
                  {chg!=null&&(
                    <span style={{fontSize:10,fontWeight:800,color:good?"#22c55e":"#ef4444"}}>
                      {chg>=0?"▲":"▼"}{Math.abs(chg).toFixed(2)}%
                    </span>
                  )}
                </span>
              );
            })}
            {!panelOpen&&(
              <span style={{fontSize:10,fontWeight:700,color:T.text3}}>← Click › to open metrics</span>
            )}
          </div>

          {/* Canvas */}
          <div style={{position:"relative",flex:1,minHeight:320}}>
            {!panelOpen&&(
              <button onClick={()=>setPanelOpen(true)}
                title="Expand metrics panel"
                style={{
                  position:"absolute",left:-16,top:"50%",transform:"translateY(-50%)",
                  zIndex:20,width:28,height:48,borderRadius:"0 8px 8px 0",
                  border:`1px solid ${T.sidebarBord}`,
                  borderLeft:"none",
                  background:T.sidebar,
                  cursor:"pointer",color:"rgba(255,255,255,0.6)",
                  fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",
                  flexShrink:0,transition:"all 0.18s",
                  boxShadow:"2px 0 8px rgba(0,0,0,0.2)"
                }}
                onMouseEnter={e=>{e.currentTarget.style.color="#fff";e.currentTarget.style.background=TREND_INDIGO;}}
                onMouseLeave={e=>{e.currentTarget.style.color="rgba(255,255,255,0.6)";e.currentTarget.style.background=T.sidebar;}}>
                ›
              </button>
            )}
            {loading&&(
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",zIndex:2}}>
                <div style={{width:26,height:26,border:`2px solid ${T.divider}`,borderTop:`2px solid ${TREND_INDIGO}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              </div>
            )}
            {!loading&&selMetrics.length===0&&(
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <p style={{color:T.text3,fontSize:12,textAlign:"center"}}>
                  {panelOpen?"← Select a metric from the left":'Click "+ Metrics" to open the panel'}
                </p>
              </div>
            )}
            <canvas ref={canvasRef} style={{display:selMetrics.length>0&&!loading?"block":"none"}}/>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ═══ DASHBOARD — IDENTICAL TO v6.1 ══════════════════════════════
// ══════════════════════════════════════════════════════════════════

export default function Dashboard(){
  const navigate = useNavigate();

  const [year,setYear]       = useState(2026);
  const [month,setMonth]     = useState("JAN");
  const [quarter,setQuarter] = useState(null);
  const [last3On,setLast3On] = useState(false);
  const [halfYear,setHalfYear] = useState(null);
  const [activeFilter,setActiveFilter] = useState("month");

  const [dark,setDark]         = useState(false);
  const [collapsed,setCollapsed]= useState(false);
  const [activeNav,setActiveNav]= useState("overview");
  const T = dark ? DARK : LIGHT;
  dark_ref = dark;

  const [pnl,setPnl]         = useState(null);
  const [eff,setEff]         = useState(null); // eslint-disable-line no-unused-vars
  const [ratios,setRatios]   = useState(null);
  const [revB,setRevB]       = useState(null);
  const [cogsB,setCogsB]     = useState(null);
  const [opexB,setOpexB]     = useState(null);
  const [expItems,setExpItems] = useState(null);
  const [pExpItems,setPExpItems] = useState(null);
  const [pPnl,setPPnl]       = useState(null);
  const [pEff,setPEff]       = useState(null); // eslint-disable-line no-unused-vars
  const [pRatios,setPRatios] = useState(null);
  const [pRevB,setPRevB]     = useState(null);
  const [pCogsB,setPCogsB]   = useState(null);
  const [pOpexB,setPOpexB]   = useState(null);
  const [hcB,setHcB]         = useState(null);
  const [pHcB,setPHcB]       = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError]     = useState(null);
  const [updated,setUpdated] = useState(null);

  const buildUrls = useCallback((af, m, y, q, hy=null) => {
    if (af === "halfyear" && hy) {
      return {
        pnl:`${API}/api/pnl/quarter?quarter=${hy}&year=${y}`,
        eff:`${API}/api/efficiency/quarter?quarter=${hy}&year=${y}`,
        rat:`${API}/api/ratios/quarter?quarter=${hy}&year=${y}`,
        rev:`${API}/api/revenue-breakdown/quarter?quarter=${hy}&year=${y}`,
        cogs:`${API}/api/cogs-breakdown/quarter?quarter=${hy}&year=${y}`,
        opex:`${API}/api/opex-breakdown/quarter?quarter=${hy}&year=${y}`,
        hc:`${API}/api/headcount-breakdown/quarter?quarter=${hy}&year=${y}`,
      };
    }
    if (af === "quarter" && q) {
      return {
        pnl:`${API}/api/pnl/quarter?quarter=${q}&year=${y}`,
        eff:`${API}/api/efficiency/quarter?quarter=${q}&year=${y}`,
        rat:`${API}/api/ratios/quarter?quarter=${q}&year=${y}`,
        rev:`${API}/api/revenue-breakdown/quarter?quarter=${q}&year=${y}`,
        cogs:`${API}/api/cogs-breakdown/quarter?quarter=${q}&year=${y}`,
        opex:`${API}/api/opex-breakdown/quarter?quarter=${q}&year=${y}`,
        hc:`${API}/api/headcount-breakdown/quarter?quarter=${q}&year=${y}`,
      };
    }
    if (af === "last3") {
      return {
        pnl:`${API}/api/pnl/last3?month=${m}&year=${y}`,
        eff:`${API}/api/efficiency/last3?month=${m}&year=${y}`,
        rat:`${API}/api/ratios/last3?month=${m}&year=${y}`,
        rev:`${API}/api/revenue-breakdown/last3?month=${m}&year=${y}`,
        cogs:`${API}/api/cogs-breakdown/last3?month=${m}&year=${y}`,
        opex:`${API}/api/opex-breakdown/last3?month=${m}&year=${y}`,
        hc:`${API}/api/headcount-breakdown/last3?month=${m}&year=${y}`,
      };
    }
    return {
      pnl:`${API}/api/pnl?month=${m}&year=${y}`,
      eff:`${API}/api/efficiency?month=${m}&year=${y}`,
      rat:`${API}/api/ratios?month=${m}&year=${y}`,
      rev:`${API}/api/revenue-breakdown?month=${m}&year=${y}`,
      cogs:`${API}/api/cogs-breakdown?month=${m}&year=${y}`,
      opex:`${API}/api/opex-breakdown?month=${m}&year=${y}`,
      hc:`${API}/api/headcount-breakdown?month=${m}&year=${y}`,
    };
  }, []);

  const getPrevMonth = useCallback((m, y) => {
    const mi = MONTHS.indexOf(m);
    if (mi > 0) return { pm: MONTHS[mi-1], py: y };
    if (y > YEARS[0]) return { pm: "DEC", py: y-1 };
    return { pm: null, py: y };
  }, []);

  const getPrevPeriodUrls = useCallback((af, m, y, q, hy) => {
    if (af === "quarter" && q) {
      const qIdx = ["Q1","Q2","Q3","Q4"].indexOf(q);
      if (qIdx > 0) {
        const prevQ = ["Q1","Q2","Q3","Q4"][qIdx - 1];
        return { quarter: prevQ, year: y };
      } else {
        return { quarter: "Q4", year: y - 1 };
      }
    }
    if (af === "halfyear" && hy) {
      return hy === "H1"
        ? { quarter: "H2", year: y - 1 }
        : { quarter: "H1", year: y };
    }
    if (af === "last3") {
      const idx = MONTHS.indexOf(m.toUpperCase());
      let endIdx = idx - 3;
      let endYear = y;
      if (endIdx < 0) { endIdx += 12; endYear = y - 1; }
      return { last3month: MONTHS[endIdx], last3year: endYear };
    }
    return null;
  }, []);

  const load = useCallback(async (af, m, y, q, hy=null) => {
    setPPnl(null);setPEff(null);setPRatios(null);
    setPRevB(null);setPCogsB(null);setPOpexB(null);setPHcB(null);
    setLoading(true); setError(null);
    try {
      const urls = buildUrls(af, m, y, q, hy);
      const [p,e,r,rb,cb,ob,,ei,hc] = await Promise.all([
        fetch(urls.pnl).then(x=>x.json()),
        fetch(urls.eff).then(x=>x.json()),
        fetch(urls.rat).then(x=>x.json()),
        fetch(urls.rev).then(x=>x.json()),
        fetch(urls.cogs).then(x=>x.json()),
        fetch(urls.opex).then(x=>x.json()),
        fetch(`${API}/api/chart-data`).then(x=>x.json()),
        (()=>{
          const base = af==="quarter"&&q ? `${API}/api/expense-items/quarter?quarter=${q}&year=${y}`
                     : af==="halfyear"&&hy ? `${API}/api/expense-items/quarter?quarter=${hy}&year=${y}`
                     : af==="last3"       ? `${API}/api/expense-items/last3?month=${m}&year=${y}`
                     : `${API}/api/expense-items?month=${m}&year=${y}`;
          return fetch(base).then(x=>x.json()).catch(()=>({}));
        })(),
        fetch(urls.hc).then(x=>x.json()).catch(()=>({})),
      ]);
      setPnl(p);setEff(e);setRatios(r);setRevB(rb);setCogsB(cb);setOpexB(ob);setExpItems(ei);setHcB(hc);
      setUpdated(new Date());

      let prevUrls = null;
      if (af === "quarter" && q) {
        const prev = getPrevPeriodUrls(af, m, y, q, hy);
        if (prev) prevUrls = {
          pnl:`${API}/api/pnl/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          eff:`${API}/api/efficiency/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          rat:`${API}/api/ratios/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          rev:`${API}/api/revenue-breakdown/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          cogs:`${API}/api/cogs-breakdown/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          opex:`${API}/api/opex-breakdown/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          exp:`${API}/api/expense-items/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          hc:`${API}/api/headcount-breakdown/quarter?quarter=${prev.quarter}&year=${prev.year}`,
        };
      } else if (af === "halfyear" && hy) {
        const prev = getPrevPeriodUrls(af, m, y, q, hy);
        if (prev) prevUrls = {
          pnl:`${API}/api/pnl/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          eff:`${API}/api/efficiency/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          rat:`${API}/api/ratios/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          rev:`${API}/api/revenue-breakdown/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          cogs:`${API}/api/cogs-breakdown/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          opex:`${API}/api/opex-breakdown/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          exp:`${API}/api/expense-items/quarter?quarter=${prev.quarter}&year=${prev.year}`,
          hc:`${API}/api/headcount-breakdown/quarter?quarter=${prev.quarter}&year=${prev.year}`,
        };
      } else if (af === "last3") {
        const prev = getPrevPeriodUrls(af, m, y, q, hy);
        if (prev) prevUrls = {
          pnl:`${API}/api/pnl/last3?month=${prev.last3month}&year=${prev.last3year}`,
          eff:`${API}/api/efficiency/last3?month=${prev.last3month}&year=${prev.last3year}`,
          rat:`${API}/api/ratios/last3?month=${prev.last3month}&year=${prev.last3year}`,
          rev:`${API}/api/revenue-breakdown/last3?month=${prev.last3month}&year=${prev.last3year}`,
          cogs:`${API}/api/cogs-breakdown/last3?month=${prev.last3month}&year=${prev.last3year}`,
          opex:`${API}/api/opex-breakdown/last3?month=${prev.last3month}&year=${prev.last3year}`,
          exp:`${API}/api/expense-items/last3?month=${prev.last3month}&year=${prev.last3year}`,
          hc:`${API}/api/headcount-breakdown/last3?month=${prev.last3month}&year=${prev.last3year}`,
        };
      } else {
        const { pm, py } = getPrevMonth(m, y);
        if (pm) prevUrls = {
          pnl:`${API}/api/pnl?month=${pm}&year=${py}`,
          eff:`${API}/api/efficiency?month=${pm}&year=${py}`,
          rat:`${API}/api/ratios?month=${pm}&year=${py}`,
          rev:`${API}/api/revenue-breakdown?month=${pm}&year=${py}`,
          cogs:`${API}/api/cogs-breakdown?month=${pm}&year=${py}`,
          opex:`${API}/api/opex-breakdown?month=${pm}&year=${py}`,
          exp:`${API}/api/expense-items?month=${pm}&year=${py}`,
          hc:`${API}/api/headcount-breakdown?month=${pm}&year=${py}`,
        };
      }

      if (prevUrls) {
        try {
          const [pp,pe,pr,prb,pcb,pob,pei,phc] = await Promise.all([
            fetch(prevUrls.pnl ).then(x=>x.json()),
            fetch(prevUrls.eff ).then(x=>x.json()),
            fetch(prevUrls.rat ).then(x=>x.json()),
            fetch(prevUrls.rev ).then(x=>x.json()),
            fetch(prevUrls.cogs).then(x=>x.json()),
            fetch(prevUrls.opex).then(x=>x.json()),
            fetch(prevUrls.exp ).then(x=>x.json()).catch(()=>({})),
            fetch(prevUrls.hc  ).then(x=>x.json()).catch(()=>({})),
          ]);
          if(pp?.grossRevenue!=null){setPPnl(pp);setPEff(pe);setPRatios(pr);setPRevB(prb);setPCogsB(pcb);setPOpexB(pob);setPExpItems(pei);setPHcB(phc);}
        } catch {}
      }
    } catch { setError("Cannot connect. Run: node server.js"); }
    finally { setLoading(false); }
  }, [buildUrls, getPrevMonth, getPrevPeriodUrls]);

  useEffect(() => { load(activeFilter, month, year, quarter, halfYear); }, [activeFilter, month, year, quarter, halfYear, load]);
  useEffect(() => {
    const t = setInterval(() => load(activeFilter, month, year, quarter, halfYear), 5*60*1000);
    return () => clearInterval(t);
  }, [activeFilter, month, year, quarter, halfYear, load]);

  // const scrollTo = (id, key) => {
  //   setActiveNav(key);
  //   setTimeout(() => {
  //     const el = document.getElementById(id);
  //     if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  //   }, 50);
  // };
const scrollTo = (id, key) => {
  if (key === 'upload') { setActiveNav('upload'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setActiveNav(key);
    setTimeout(() => {
      if (id === "top-nav") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const { pm: prevMonth, py: prevYear } = getPrevMonth(month, year);

  const ebitaPct  = pnl?.ebitaPct  != null ? pnl.ebitaPct
                  : (pnl?.grossRevenue && pnl?.ebita!=null) ? ((pnl.ebita/pnl.grossRevenue)*100) : null;
  const pEbitaPct = pPnl?.ebitaPct != null ? pPnl.ebitaPct
                  : (pPnl?.grossRevenue && pPnl?.ebita!=null) ? ((pPnl.ebita/pPnl.grossRevenue)*100) : null;
  const netRev    = pnl?.grossRevenue!=null  ? pnl.grossRevenue  - (pnl?.cogs??0) : null;
  const pNetRev   = pPnl?.grossRevenue!=null ? pPnl.grossRevenue - (pPnl?.cogs??0) : null;
  const netRevPerEmp  = eff?.netRevPerEmp  ?? (netRev!=null  && pnl?.headCount  ? netRev/pnl.headCount   : null);
  const pNetRevPerEmp = pEff?.netRevPerEmp ?? (pNetRev!=null && pPnl?.headCount ? pNetRev/pPnl.headCount : null);
  const costPerEmp    = eff?.costPerEmp    ?? (pnl?.opex!=null  && pnl?.headCount  ? pnl.opex/pnl.headCount   : null);
  const pCostPerEmp   = pEff?.costPerEmp   ?? (pPnl?.opex!=null && pPnl?.headCount ? pPnl.opex/pPnl.headCount : null);

  const filterLabel = activeFilter === "halfyear" && halfYear
    ? `${halfYear} ${year} (${HALF_YEARS.find(h=>h.value===halfYear)?.label || ""})`
    : activeFilter === "quarter" && quarter
    ? `${quarter} ${year} (${QUARTERS.find(q=>q.value===quarter)?.months.join("+") || ""})`
    : activeFilter === "last3" && last3On
    ? `Last 3 months ending ${month} ${year}`
    : `${toMon(month)} ${year}`;

  const NAV = [
    // {icon:"",label:"OVERVIEW",key:"overview",target:"section-pnl"},
    {icon:"",label:"OVERVIEW",key:"overview",target:"top-nav"},
    {icon:"",label:"REVENUE", key:"revenue", target:"section-revenue"},
    {icon:"",label:"COGS",    key:"cogs",    target:"section-cogs"},
    {icon:"",label:"OPEX",    key:"opex",    target:"section-opex"},
    {icon:"",label:"RATIOS",  key:"ratios",  target:"section-ratios"},
    {icon:"",label:"TRENDS",  key:"trends",  target:"section-trends"},
{icon:"",label:"UPLOAD",  key:"upload",  target:"upload-page"},
];

  return(
    <div style={{display:"flex",minHeight:"100vh",background:T.bgGrad,
      fontFamily:"Plus Jakarta Sans,sans-serif",color:T.text1,transition:"background 0.4s"}}>

      {/* SIDEBAR */}
      <div style={{width:collapsed?64:210,flexShrink:0,background:T.sidebar,
        borderRight:`1px solid ${T.sidebarBord}`,display:"flex",flexDirection:"column",
        position:"sticky",top:0,height:"100vh",zIndex:100,
        transition:"width 0.28s cubic-bezier(0.4,0,0.2,1)",overflow:"hidden"}}>
        <div style={{padding:collapsed?"14px 8px":"18px 16px 14px",
          borderBottom:"1px solid rgba(255,255,255,0.07)",
          display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          {!collapsed&&(
            <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>navigate("/")}>
              <img src="/logos/tsi-logo.jpg" alt="TSI" style={{width:36,height:"auto",borderRadius:8}}
                onError={e=>{e.target.style.display="none";}}/>
              <div style={{fontSize:16,fontWeight:900,color:"#fff",letterSpacing:4}}>TSI</div>
            </div>
          )}
          {collapsed&&(
            <div style={{cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>navigate("/")}>
              <img src="/logos/tsi-logo.jpg" alt="TSI" style={{width:32,height:32,borderRadius:8,objectFit:"contain"}}
                onError={e=>{e.target.style.display="none";}}/>
            </div>
          )}
          <button onClick={()=>setCollapsed(c=>!c)} style={{
            width:28,height:28,borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",
            background:"rgba(255,255,255,0.04)",cursor:"pointer",color:"rgba(255,255,255,0.4)",
            fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {collapsed?"›":"‹"}
          </button>
        </div>
        <div style={{padding:collapsed?"10px 6px":"14px 10px",flex:1,overflowY:"auto"}}>
          {NAV.map(n=>(
            <NavItem key={n.key} icon={n.icon} label={n.label}
              isActive={activeNav===n.key}
              onClick={()=>scrollTo(n.target,n.key)}
              T={T} collapsed={collapsed}/>
          ))}
        </div>
        {!collapsed&&(
          <div style={{padding:"10px 16px 16px",borderTop:"1px solid rgba(255,255,255,0.05)"}}>
            <div onClick={()=>navigate("/")}
              style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:8,cursor:"pointer"}}
              onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.05)";}}
              onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontWeight:900}}>←</span>
              <span style={{fontSize:11,fontWeight:900,color:"rgba(255,255,255,0.6)",letterSpacing:1.5,textTransform:"uppercase"}}>Home</span>
            </div>
            <p style={{margin:0,fontSize:8,color:"rgba(255,255,255,0.14)",letterSpacing:1.5}}>TSI DASHBOARD v6.5</p>
          </div>
        )}
      </div>

      {/* MAIN */}
      <div data-main-scroll="1" style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",position:"relative"}}>

        {/* TOP NAV */}
        <div id="top-nav" style={{position:"sticky",top:0,zIndex:50,background:T.navBg,
          borderBottom:`1px solid ${T.navBorder}`}}>
          <div style={{padding:"12px 26px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <h1 style={{margin:0,fontSize:17,fontWeight:900,color:"#fff"}}>Business Dashboard</h1>
              <span style={{fontSize:9,padding:"3px 11px",borderRadius:20,
                background:"rgba(34,197,94,0.14)",color:"#22c55e",fontWeight:700,
                border:"1px solid rgba(34,197,94,0.3)"}}>● LIVE</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:error?"#f43f5e":"#22c55e"}}/>
                <span style={{fontSize:10.5,color:"rgba(255,255,255,0.65)",fontWeight:700}}>
                  {error?"API Error":loading?"Loading…":`Showing: ${filterLabel}`}
                </span>
              </div>
              {updated&&<span style={{fontSize:8.5,color:"rgba(255,255,255,0.2)"}}>↻ {updated.toLocaleTimeString()}</span>}
              <button onClick={()=>load(activeFilter,month,year,quarter)} style={{
                padding:"6px 17px",borderRadius:9,
                background:`linear-gradient(135deg,rgba(249,115,22,0.22),rgba(249,115,22,0.12))`,
                border:`1px solid rgba(249,115,22,0.38)`,color:"#fff",fontSize:10,cursor:"pointer",fontWeight:700}}>
                ↻ REFRESH
              </button>
            </div>
          </div>

          {/* FILTER BAR */}
          {activeNav !== 'upload' && (
          <div style={{padding:"10px 26px",borderTop:"1px solid rgba(255,255,255,0.06)",
            display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",
            background:"rgba(0,0,0,0.18)"}}>
            <div style={{display:"flex",alignItems:"center",gap:0,
              background:"rgba(249,115,22,0.08)",borderRadius:10,
              border:"1px solid rgba(249,115,22,0.35)",overflow:"hidden"}}>
              <select value={month} onChange={e=>{setMonth(e.target.value);setActiveFilter("month");setLast3On(false);setQuarter(null);}}
                style={{height:34,padding:"0 10px",background:"transparent",
                  border:"none",color:TSI_ORANGE,
                  fontSize:12,fontWeight:800,cursor:"pointer",outline:"none",
                  borderRight:"1px solid rgba(249,115,22,0.2)"}}>
                {MONTHS.map(m=><option key={m} value={m} style={{background:"#0c1a3a",color:"#fff"}}>{m}</option>)}
              </select>
              <select value={year} onChange={e=>{setYear(+e.target.value);}}
                style={{height:34,padding:"0 10px",background:"transparent",
                  border:"none",color:TSI_ORANGE,
                  fontSize:12,fontWeight:800,cursor:"pointer",outline:"none"}}>
                {YEARS.map(y=><option key={y} value={y} style={{background:"#0c1a3a",color:"#fff"}}>{y}</option>)}
              </select>
            </div>
            <div style={{width:1,height:22,background:"rgba(255,255,255,0.1)",margin:"0 6px"}}/>
            <div style={{display:"flex",alignItems:"center",gap:0,
              background:activeFilter==="quarter"?"rgba(59,130,246,0.15)":"rgba(59,130,246,0.06)",
              borderRadius:10,border:`1px solid ${activeFilter==="quarter"?"rgba(59,130,246,0.5)":"rgba(59,130,246,0.2)"}`,
              overflow:"hidden",transition:"all 0.2s"}}>
              <select
                value={activeFilter==="quarter"?quarter||"":" "}
                onChange={e=>{
                  const v=e.target.value;
                  if(!v||v===" "){setQuarter(null);setActiveFilter("month");}
                  else{setQuarter(v);setActiveFilter("quarter");setLast3On(false);}
                }}
                style={{height:34,padding:"0 10px",background:"transparent",
                  border:"none",
                  color:activeFilter==="quarter"?"#60a5fa":"rgba(96,165,250,0.7)",
                  fontSize:12,fontWeight:800,cursor:"pointer",outline:"none",minWidth:140}}>
                <option value=" " style={{background:"#0c1a3a",color:"#94a3b8"}}>QUARTERLY</option>
                {QUARTERS.map(q=>(
                  <option key={q.value} value={q.value} style={{background:"#0c1a3a",color:"#fff"}}>{q.label}</option>
                ))}
              </select>
            </div>
            <div style={{width:1,height:22,background:"rgba(255,255,255,0.1)",margin:"0 6px"}}/>
            <div onClick={()=>{
                const newVal=!last3On;
                setLast3On(newVal);
                setActiveFilter(newVal?"last3":"month");
                if(newVal){setQuarter(null);}
              }}
              style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",height:34,
                padding:"0 14px",borderRadius:10,
                background:last3On?"rgba(34,197,94,0.15)":"rgba(34,197,94,0.06)",
                border:`1px solid ${last3On?"rgba(34,197,94,0.5)":"rgba(34,197,94,0.2)"}`,
                transition:"all 0.22s",userSelect:"none"}}>
              <div style={{width:15,height:15,borderRadius:4,flexShrink:0,
                background:last3On?"#22c55e":"transparent",
                border:`2px solid ${last3On?"#22c55e":"rgba(255,255,255,0.25)"}`,
                display:"flex",alignItems:"center",justifyContent:"center",
                transition:"all 0.2s"}}>
                {last3On&&<span style={{color:"#fff",fontSize:10,fontWeight:900,lineHeight:1}}>✓</span>}
              </div>
              <span style={{fontSize:11,fontWeight:800,whiteSpace:"nowrap",
                color:last3On?"#22c55e":"rgba(255,255,255,0.5)",
                transition:"color 0.2s"}}>Last 3 Months</span>
              {last3On&&(
                <span style={{fontSize:9,color:"rgba(34,197,94,0.6)",fontWeight:700,
                  whiteSpace:"nowrap",marginLeft:2}}>
                  ({getLast3Label(month,year)})
                </span>
              )}
            </div>
            <div style={{width:1,height:22,background:"rgba(255,255,255,0.1)",margin:"0 6px"}}/>
            <div style={{display:"flex",alignItems:"center",gap:0,
              background:activeFilter==="halfyear"?"rgba(245,158,11,0.15)":"rgba(245,158,11,0.06)",
              borderRadius:10,
              border:`1px solid ${activeFilter==="halfyear"?"rgba(245,158,11,0.5)":"rgba(245,158,11,0.2)"}`,
              overflow:"hidden",transition:"all 0.2s"}}>
              <select
                value={activeFilter==="halfyear"?halfYear||"":" "}
                onChange={e=>{
                  const v=e.target.value;
                  if(!v||v===" "){setHalfYear(null);setActiveFilter("month");}
                  else{setHalfYear(v);setActiveFilter("halfyear");setLast3On(false);setQuarter(null);}
                }}
                style={{height:34,padding:"0 12px",background:"transparent",
                  border:"none",
                  color:activeFilter==="halfyear"?"#f59e0b":"rgba(245,158,11,0.7)",
                  fontSize:12,fontWeight:800,cursor:"pointer",outline:"none",minWidth:150}}>
                <option value=" " style={{background:"#0c1a3a",color:"#94a3b8"}}>BI-YEARLY</option>
                {HALF_YEARS.map(h=>(
                  <option key={h.value} value={h.value} style={{background:"#0c1a3a",color:"#fff"}}>{h.label}</option>
                ))}
              </select>
            </div>
            <div style={{flex:1}}/>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {activeFilter==="month"&&!last3On&&(
                <span style={{fontSize:10,fontWeight:700,padding:"4px 12px",borderRadius:20,
                  background:`${TSI_ORANGE}18`,color:TSI_ORANGE,
                  border:`1px solid ${TSI_ORANGE}35`}}>
                  {month} {year}
                </span>
              )}
              {activeFilter==="quarter"&&quarter&&(
                <span style={{fontSize:10,fontWeight:700,padding:"4px 12px",borderRadius:20,
                  background:"rgba(99,102,241,0.15)",color:"#818cf8",
                  border:"1px solid rgba(99,102,241,0.3)"}}>
                  {quarter} · {year}
                </span>
              )}
              {last3On&&(
                <span style={{fontSize:10,fontWeight:700,padding:"4px 12px",borderRadius:20,
                  background:"rgba(34,197,94,0.15)",color:"#22c55e",
                  border:"1px solid rgba(34,197,94,0.3)"}}>
                  Last 3M · {month} {year}
                </span>
              )}
              {activeFilter==="halfyear"&&halfYear&&(
                <span style={{fontSize:10,fontWeight:700,padding:"4px 12px",borderRadius:20,
                  background:"rgba(245,158,11,0.15)",color:"#f59e0b",
                  border:"1px solid rgba(245,158,11,0.3)"}}>
                  {halfYear} · {year}
                </span>
              )}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:8}}>
              <span style={{fontSize:16,lineHeight:1}}>{dark?"🌙":"☀️"}</span>
              <div onClick={()=>setDark(d=>!d)} style={{width:40,height:22,borderRadius:11,cursor:"pointer",
                position:"relative",flexShrink:0,
                background:dark?`linear-gradient(135deg,${TSI_ORANGE},${TSI_RED})`:"rgba(255,255,255,0.2)",
                transition:"background 0.3s",border:"1px solid rgba(255,255,255,0.1)"}}>
                <div style={{position:"absolute",top:3,width:16,height:16,borderRadius:"50%",
                  background:"#fff",transition:"left 0.28s cubic-bezier(0.34,1.56,0.64,1)",
                  left:dark?21:3,boxShadow:"0 1px 4px rgba(0,0,0,0.3)"}}/>
              </div>
            </div>
          </div>
          )}
        </div>

 {/* BODY */}
        <div style={{padding:"20px 24px",flex:1}}>
          {activeNav==='upload' && <UploadPage />}
          {activeNav!=='upload' && error&&(
            <div style={{background:"rgba(244,63,94,0.08)",border:"1px solid rgba(244,63,94,0.22)",borderRadius:16,padding:"28px",textAlign:"center",maxWidth:500,margin:"0 auto"}}>
              <p style={{fontSize:30,margin:0}}>⚠️</p>
              <p style={{color:T.red,fontWeight:700,margin:"10px 0 4px"}}>{error}</p>
            </div>
          )}
          {activeNav!=='upload' && !error&&loading&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"80px 0",flexDirection:"column",gap:16}}>
              <div style={{width:44,height:44,border:`3px solid ${T.divider}`,borderTop:`3px solid ${TSI_ORANGE}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              <p style={{color:T.text3,fontSize:10,letterSpacing:3,margin:0,fontWeight:700}}>LOADING DATA</p>
            </div>
          )}
          {activeNav!=='upload' && !error&&!loading&&(
            <div style={{display:"flex",flexDirection:"column",gap:18}}>
              {(()=>{
                const mwd = pnl?.monthsWithData || 1;
                const tl = buildTileLabels(activeFilter, month, year, quarter, halfYear, mwd);
                return (
                  <div style={{display:"flex",gap:12}}>
                    <KpiTile label="Gross Revenue" value={pnl?.grossRevenue} prev={pPnl?.grossRevenue} color={TSI_ORANGE} T={T}
                      periodLabel={tl.periodLabel} subLabel={tl.sumLabel("Gross Revenue")}
                      onClick={()=>scrollTo("section-revenue","revenue")}/>
                    <KpiTile label="COGS" value={pnl?.cogs} prev={pPnl?.cogs} color={TSI_RED} lower T={T}
                      periodLabel={tl.periodLabel} subLabel={tl.sumLabel("COGS")}
                      onClick={()=>scrollTo("section-cogs","cogs")}/>
                    <KpiTile label="Total OPEX" value={pnl?.opex} prev={pPnl?.opex} color={TSI_BLUE} lower T={T}
                      periodLabel={tl.periodLabel} subLabel={tl.sumLabel("Total OPEX")}
                      onClick={()=>scrollTo("section-opex","opex")}/>
                    <KpiTile label="EBITA %" value={ebitaPct} prev={pEbitaPct} color="#22c55e" T={T} fmt={fmtEbita}
                      periodLabel={tl.periodLabel}subLabel={tl.avgLabel("EBITA %")} 
                      onClick={()=>scrollTo("section-trends","trends")}/>
                    <KpiTile label="Head Count" value={pnl?.headCount} prev={pPnl?.headCount} color="#a855f7" fmt={fmtInt} T={T} icon="👥" showIcon
                      periodLabel={tl.periodLabel}subLabel={tl.avgLabel("Head Count")} />
                  </div>
                );
              })()}

              <SCard id="section-pnl" title="Financial Summary" subtitle={getCardSubtitle(activeFilter,month,year,quarter,halfYear,"sum")} accent={TSI_ORANGE} T={T}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr"}}>
                  <div style={{borderRight:`1px solid ${T.divider}`}}>
                    {[
                      {label:"Salaries",         value:expItems?.salaries,         prev:pExpItems?.salaries,         lower:true},
                      {label:"Contract Services",value:expItems?.contractServices, prev:pExpItems?.contractServices, lower:true},
                      {label:"Benefits",         value:expItems?.benefits,         prev:pExpItems?.benefits,         lower:true},
                      {label:"Travel",           value:expItems?.travel,           prev:pExpItems?.travel,           lower:true},
                      {label:"Bonus Accrual",    value:expItems?.bonusAccrual,     prev:pExpItems?.bonusAccrual,     lower:true},
                    ].map((item,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",
                        padding:"14px 20px",minHeight:52,transition:"background 0.18s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background=T.rowHover;}}
                        onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
                        <span style={{fontSize:12,fontWeight:700,flex:1,color:T.text2,letterSpacing:0.3}}>{item.label}</span>
                        <ValRow value={item.value} prev={item.prev} lower={item.lower} T={T}/>
                      </div>
                    ))}
                  </div>
                  <div>
                    {[
                      {label:"Net Revenue",        value:netRev,       prev:pNetRev,       fmt:fmtM,   lower:false},
                      {label:"Cost / Employee",    value:costPerEmp,   prev:pCostPerEmp,   fmt:fmtLoc, lower:true},
                      {label:"Net Rev / Employee", value:netRevPerEmp, prev:pNetRevPerEmp, fmt:fmtLoc, lower:false},
                    ].map((item,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",
                        padding:"14px 20px",minHeight:52,transition:"background 0.18s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background=T.rowHover;}}
                        onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
                        <span style={{fontSize:12,fontWeight:700,flex:1,color:T.text2,letterSpacing:0.3}}>{item.label}</span>
                        <ValRow value={item.value} prev={item.prev} lower={item.lower??false} T={T} fmt={item.fmt}/>
                      </div>
                    ))}
                  </div>
                </div>
              </SCard>

              <div style={{display:"flex",gap:16}}>
                <SCard id="section-revenue" title="Gross Revenue" subtitle={getCardSubtitle(activeFilter,month,year,quarter,halfYear,"sum")} accent={TSI_ORANGE} T={T} totalValue={pnl?.grossRevenue}>
                  {revB&&Object.entries(revB).map(([k,v])=>(
                    <DrillRow key={k} label={k} value={v} prev={pRevB?.[k]} type="Gross Revenue" revenueType={k} month={month} year={year} pmonth={prevMonth} pyear={prevYear} T={T}/>
                  ))}
                  {revB&&Object.values(revB).some(v=>Math.abs(v)>0)&&(<div style={{borderTop:`1px solid ${T.divider}`}}><FullDonut data={revB} colors={REV_C} T={T}/></div>)}
                </SCard>
                <SCard id="section-cogs" title="COGS" subtitle={getCardSubtitle(activeFilter,month,year,quarter,halfYear,"sum")} accent={TSI_RED} T={T} totalValue={pnl?.cogs}>
                  {cogsB&&Object.entries(cogsB).map(([k,v])=>(
                    <DrillRow key={k} label={k} value={v} prev={pCogsB?.[k]} type="COGS" revenueType={k} month={month} year={year} pmonth={prevMonth} pyear={prevYear} T={T} lower/>
                  ))}
                  {cogsB&&Object.values(cogsB).some(v=>Math.abs(v)>0)&&(<div style={{borderTop:`1px solid ${T.divider}`}}><FullDonut data={cogsB} colors={COGS_C} T={T}/></div>)}
                </SCard>
              </div>

              <div style={{display:"flex",gap:16}}>
                <SCard id="section-opex" title="OPEX" subtitle={getCardSubtitle(activeFilter,month,year,quarter,halfYear,"sum")} accent={TSI_BLUE} T={T} totalValue={pnl?.opex}>
                  {opexB&&Object.entries(opexB).map(([k,v])=>(
                    <DrillRow key={k} label={k} value={v} prev={pOpexB?.[k]} type="Total OPEX" revenueType={k} month={month} year={year} pmonth={prevMonth} pyear={prevYear} T={T} lower/>
                  ))}
                  {opexB&&Object.values(opexB).some(v=>Math.abs(v)>0)&&(<div style={{borderTop:`1px solid ${T.divider}`}}><FullDonut data={opexB} colors={OPEX_C} T={T}/></div>)}
                </SCard>
                <SCard id="section-ratios" title="Departmental Ratio" subtitle={getCardSubtitle(activeFilter,month,year,quarter,halfYear,"avg")} accent={TSI_BLUE} T={T}>
                  {/* {ratios && Object.entries(ratios).map(([label, item]) => {
                    const isAdmin = item?.isPercent === true;
                    const val     = item?.display ?? item;
                    const prevItem = pRatios?.[label];
                    const prevVal  = prevItem?.display ?? prevItem;
                    return (
                      <DataRow key={label} label={label}
                        value={val} prev={prevVal} lower={true}
                        fmt={isAdmin
                          ? v => v!=null ? `${Number(v).toFixed(2)}%` : "—"
                          : v => v!=null ? `${Number(v).toFixed(2)}` : "—"}
                        T={T}/>
                    );
                  })} */}
                    {ratios && Object.entries(ratios).map(([label, item]) => {
                    const isAdmin = item?.isPercent === true;
                    const val     = item?.display ?? item;
                    const prevItem = pRatios?.[label];
                    const prevVal  = prevItem?.display ?? prevItem;

                    // Mod 1: value colour
                    const THRESHOLDS = {
                      "Maintenance (>4)":           v => v >  4,
                      "Sales & Marketing (>1.5)":   v => v >  1.5,
                      "R&D (>1)":                   v => v >  1,
                      "Professional Services (>2)": v => v >  2,
                      "Administration (<15%)":      v => v < 15,
                    };
                    const passes     = THRESHOLDS[label] && val != null ? THRESHOLDS[label](val) : null;
                    const valueColor = passes === true ? T.green : passes === false ? T.red : null;

                    // Mod 2: arrow logic
                    const isLower = label === "Administration (<15%)";

                    return (
                      <DataRow key={label} label={label}
                        value={val} prev={prevVal} lower={isLower}
                        valueColor={valueColor}
                        fmt={isAdmin
                          ? v => v!=null ? `${Number(v).toFixed(2)}%` : "—"
                          : v => v!=null ? `${Number(v).toFixed(2)}` : "—"}
                        T={T}/>
                    );
                  })}
                  {/* ── G&A and S&M Ratios (calculated from opexB / netRevenue) ──
                  {(()=>{
                    const nr = pnl?.netRevenue || pnl?.grossRevenue || 0;
                    const pNr = pPnl?.netRevenue || pPnl?.grossRevenue || 0;
                    const gaOpex  = opexB?.["General & Administration"] || 0;
                    const smOpex  = opexB?.["Sales & Marketing"] || 0;
                    const pGaOpex = pOpexB?.["General & Administration"] || 0;
                    const pSmOpex = pOpexB?.["Sales & Marketing"] || 0;
                    const gaRatio = nr > 0 ? gaOpex / nr : 0;
                    const smRatio = nr > 0 ? smOpex / nr : 0;
                    const pGaRatio = pNr > 0 ? pGaOpex / pNr : null;
                    const pSmRatio = pNr > 0 ? pSmOpex / pNr : null;
                    return (<>
                      <DataRow label="General & Admin Ratio"
                        value={gaRatio} prev={pGaRatio} lower={true}
                        fmt={v=>v!=null?`${Number(v).toFixed(2)}`:"—"} T={T}/>
                      <DataRow label="Sales & Marketing Ratio"
                        value={smRatio} prev={pSmRatio} lower={true}
                        fmt={v=>v!=null?`${Number(v).toFixed(2)}`:"—"} T={T}/>
                    </>);
                  })()} */}

                  {/* ── Departmental Headcount section header ── */}
                  <div style={{
                    borderTop:`1px solid ${T.divider}`,
                    padding:"10px 20px 8px",
                    background:`linear-gradient(110deg,rgba(168,85,247,0.12) 0%,rgba(168,85,247,0.04) 100%)`,
                    display:"flex",alignItems:"center",justifyContent:"space-between"
                  }}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:4,height:18,background:"linear-gradient(180deg,#a855f7,rgba(168,85,247,0.3))",borderRadius:3}}/>
                      <div style={{display:"flex",flexDirection:"column",gap:2}}>
                          <span style={{fontSize:10,fontWeight:900,color:"#a855f7",letterSpacing:2.5,textTransform:"uppercase"}}>Departmental Headcount</span>
                          {getCardSubtitle(activeFilter,month,year,quarter,halfYear,"avg")&&(
                            <span style={{fontSize:9.5,fontWeight:600,color:"#a855f7",opacity:0.6,letterSpacing:0.3,textTransform:"none"}}>
                              {getCardSubtitle(activeFilter,month,year,quarter,halfYear,"avg")}
                            </span>
                          )}
                        </div>
                    </div>
                    {hcB?.["Total Headcount"] != null && (
                      <span style={{fontSize:13,fontWeight:900,color:"#a855f7",fontFamily:"'JetBrains Mono',Consolas,monospace",
                        padding:"3px 12px",borderRadius:9,background:"rgba(168,85,247,0.12)",border:"1px solid rgba(168,85,247,0.28)"}}>
                        {Math.round(hcB["Total Headcount"])}
                      </span>
                    )}
                  </div>

                  {/* ── Headcount dept rows ── */}
                  {hcB && Object.entries(hcB).filter(([label]) => label !== "Total Headcount").map(([label, val]) => {
                    const prevVal = pHcB?.[label] ?? null;
                    const hasBadge = prevVal != null && prevVal !== val;
                    const up = val > prevVal;
                    return (
                      <div key={label} style={{
                        display:"flex", alignItems:"center",
                        padding:"10px 20px",
                        minHeight:44,
                        background:"transparent",
                        transition:"background 0.18s",
                      }}
                      onMouseEnter={e=>{ e.currentTarget.style.background=T.rowHover; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background="transparent"; }}>
                        <span style={{
                          fontSize:11.5, fontWeight:700, flex:1,
                          color:T.text2,
                        }}>{label}</span>
                        <div style={{
                          display:"flex", alignItems:"center",
                          justifyContent:"flex-end",
                          gap:8,
                          minWidth:110,
                        }}>
                          <span style={{
                            fontSize:13, fontWeight:900,
                            color:T.text1,
                            fontFamily:"'JetBrains Mono',Consolas,monospace",
                            minWidth:36, textAlign:"right",
                          }}>{Math.round(val)}</span>
                          <div style={{minWidth:58, display:"flex", justifyContent:"flex-start"}}>
                            {hasBadge && (
                              <span style={{
                                fontSize:9.5, fontWeight:700,
                                padding:"2px 8px", borderRadius:20,
                                background: up ? "rgba(34,197,94,0.13)" : "rgba(244,63,94,0.13)",
                                color: up ? T.green : T.red,
                                border:`1px solid ${up ? "rgba(34,197,94,0.3)" : "rgba(244,63,94,0.3)"}`,
                                whiteSpace:"nowrap",
                              }}>
                                {up ? "▲" : "▼"} {Math.abs(val - prevVal)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </SCard>
              </div>

              <TrendsCard month={month} year={year} T={T}/>

            </div>
          )}
        </div>

        <div style={{padding:"10px 24px 16px",borderTop:`1px solid ${T.divider}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:9,color:T.text3,letterSpacing:1.2}}>TSI BUSINESS DASHBOARD · {filterLabel.toUpperCase()}</span>
          <span style={{fontSize:9,color:T.text3}}>Auto-refreshes every 5 min · PostgreSQL</span>
        </div>
      </div>

  <PnLChatWidget />

      <style>{`
        @keyframes spin { to{transform:rotate(360deg)} }
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-track{background:${T.bg}}
        ::-webkit-scrollbar-thumb{background:${T.divider};border-radius:4px}
        select option{background:#0c1a3a;color:#fff}
      `}</style>
    </div>
  );
}